// OrderDetailV2.jsx
// ────────────────────────────────────────────────────────────────────────────
// Modern Order Detail view — flagged on with `?v=2`.
// Self-contained: does its own fetching of order + portal-state.
// Reuses pressingHelpers.buildBundles so bundle structure matches every other
// view in the app.
// ────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useState, useCallback, useRef, createContext, useContext } from 'react';
import { MissingEmbellishmentsBanner } from '../MissingEmbellishments';
import { useParams, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useAuth, useAuthFetch } from '../AuthComponent';
import { useStore } from '../StoreContext';
import {
    buildBundles,
    EMBELLISHMENT_TYPES,
    MISSING_IMAGE_SVG,
} from '../pressingHelpers';
import {
    psForSku, anyPickingDone,
    dotsForRow, dotsForBundle, rollupStates,
    bundleKind, isDecoration,
    STAGES, STAGE_LABELS,
} from './dotStates';
import { isVirtual } from '../virtualTitles';
import { resolveOrdersBackTo, ordersBackToLabel } from '../ordersBackTo';
import { isLabelFirstWarehouse } from '../constants/labelFirstWarehouses';
import OrderTimeline from '../OrderTimeline';
import OrderNotificationsBanner from '../OrderNotificationsBanner';
import OrderNotificationsControl from '../OrderNotificationsControl';
import RefundModal from '../RefundModal';
import ReportIssueModal from '../ReportIssueModal';
import { getPreviewImageUrls } from '../previewImage';
import SmartProductImage from '../SmartProductImage';
import NumberPreviewThumb, { buildFontFaceCss, getPreviewFor, getPrintedNumberAttr } from '../NumberPreviewThumb';
// 2026-09-15 (Eric): V1 parity — the per-fulfillment action menu, the item
// badges/links and a few helpers come straight from the classic page so the
// two never drift. (Circular import: OrderDetail lazy-renders this page; the
// bindings are only touched at render time, never at module load.)
import { FulfillmentMenu, JumbleBadge, SkuNotificationCell, SkuNotificationsContext, StoreKeyContext, TpReqBadge, EventLog,
         ProductLink, presenceColor, CANCEL_REASON_LABELS, isPickupMethod } from '../OrderDetail';

import OrderPrintCard, {
    isPrinted as isPrintedTag,
    getPerLocationPrintTags,
    buildPopupHTML,
    MergedNjPrint,
} from '../OrderPrintCard';
import { njMergeAllowed, njWarehouseLocsOnOrder, readMergeNjPref, writeMergeNjPref } from '../constants/njWarehouses';
import { Checkbox, Tag, Collapsible, BlockStack, InlineStack, Box, Text, Modal, Select, Banner, Tooltip } from '@shopify/polaris';
import './order-detail-v2.css';

import { hasRole, allRolesIn } from '../constants/roles';
// Per-page contexts so the deep row renderers can reach order-wide data
// without threading props through every table component.
const JumbleContext = createContext({});      // lineItemGroup id / line id → jumble color index
const NpContext     = createContext({});      // number-preview metaobjects (by product id)
const FoMenuContext = createContext(null);    // everything FulfillmentMenu needs per FO
const LineFlagsContext = createContext(null); // line item id → backend {picked, packed, pressed} (merged fo._lineItemFlags)
const TpReqContext = createContext(null);     // line item gid → Team Portal requirement entry (order.tp_requirements.line_items)
const TpOverridesContext = createContext(null); // { numberFor(li), teamFor(li), byPlayer(playerId, name) } from order.tp_number_overrides / tp_team_overrides

// Provided by OrderDetailV3 to make product thumbnails clickable and open a
// preview lightbox. null (the default) leaves v2 behavior identical to today.
export const ProductPreviewContext = createContext(null);

// Provided by OrderDetailV3 to inject a horizontal stages strip above the tabs.
// Receives { pickPressTotals, isOrderPrinted } and returns JSX (or null).
export const StagesStripContext = createContext(null);

// Provided by OrderDetailV3 to replace the WorkflowRail in the sidebar.
// Receives { order } and returns JSX (or null). Null = keep WorkflowRail.
export const SidebarHeadContext = createContext(null);

// Shopify Admin store handles per OpsHub store key. Mirrors OrderDetail.jsx.
const SHOPIFY_ADMIN_HANDLES = {
    soccerpost: 'soccerpostshop',
    surf:       'surf-soccerpost',
    rush:       'rush-soccerpost',
    citysports: 'citysportsnj',
    u90:        'u90soccer',
    b2b:        'soccerpostb2b',
    stefans:    'stefans-soccer',
};

// Write an HTML string into a popup window (open() → write → close).
// Centralized so the rest of the print pipeline never references the raw
// popup document API. Mirrors V1 OrderDetail's popup flow.
function writePopupHTML(popup, html) {
    if (!popup || popup.closed) return;
    const doc = popup.document;
    doc.open();
    // eslint-disable-next-line no-restricted-syntax
    doc['write'](html);
    doc.close();
}

// ── small atoms ─────────────────────────────────────────────────────────────
// `pending` flips the dot to its "desired" appearance (done if currently todo,
// todo if currently done) and adds a dashed ring so the user can see what
// will land when they hit Apply. `onClick` makes the dot a real button.
function Dot({ stage, state, label, onClick, pending }) {
    const displayState = pending
        ? (state === 'done' ? 'todo' : 'done')
        : state;
    const cls = [
        'odv2-dot',
        `odv2-dot--${displayState}`,
        `odv2-dot--stage-${stage}`,
        onClick ? 'odv2-dot--btn' : '',
        pending ? 'odv2-dot--pending' : '',
    ].filter(Boolean).join(' ');
    const title = label ? `${STAGE_LABELS[stage]} · ${label}` : STAGE_LABELS[stage];
    if (onClick) {
        return (
            <button type="button"
                    className={cls}
                    title={pending ? `${title} (pending)` : title}
                    onClick={(e) => { e.stopPropagation(); onClick(); }} />
        );
    }
    return <span className={cls} title={title} />;
}

function StageDots({ dots }) {
    return (
        <span className="odv2-dots">
            {STAGES.map(s => (
                <Dot key={s} stage={s} state={dots[s].state} label={dots[s].label} />
            ))}
        </span>
    );
}

// Render one <td> per stage so dots sit centered directly under each
// stage header column (Picked / Pressed / Packed).
//
// `clickable` is a per-stage map: { picked: bool, pressed: bool, packed: bool }
// — `onToggle(stage)` fires when a clickable dot is pressed.
// `pending` is a per-stage map: { picked: bool, pressed: bool, packed: bool }.
function StageDotCells({ dots, clickable, pending, onToggle }) {
    return (
        <>
            {STAGES.map(s => {
                const canClick = !!(clickable && clickable[s]);
                const isPending = !!(pending && pending[s]);
                return (
                    <td key={s} className="odv2-cell-center odv2-cell-stage">
                        <Dot stage={s}
                             state={dots[s].state}
                             label={dots[s].label}
                             pending={isPending}
                             onClick={canClick ? () => onToggle?.(s) : undefined} />
                    </td>
                );
            })}
        </>
    );
}

function Thumb({ src, alt, missing }) {
    if (missing || !src) {
        return (
            <div className="odv2-thumb odv2-thumb--missing">
                <img src={MISSING_IMAGE_SVG} alt={alt || ''} />
            </div>
        );
    }
    return (
        <div className="odv2-thumb">
            <SmartProductImage src={src} alt={alt || ''} />
        </div>
    );
}

// Resolve the right image(s) for a bundle parent row:
//   1. Customer preview (`Preview front` / `Preview back` from customAttributes
//      on the lineItemGroup, parent line item, or any child) — render front +
//      back side-by-side if both exist.
//   2. Otherwise the line item's own image, then the product featured image.
//
// When ProductPreviewContext is active (v3+) the whole thumb is wrapped in a
// button so clicking it opens the product-preview lightbox.
function BundleParentThumb({ b, alt }) {
    const onPreview   = useContext(ProductPreviewContext);
    const groupAttrs  = b.groupData?.customAttributes || b.parent?.lineItemGroup?.customAttributes || [];
    const parentAttrs = b.parent?.customAttributes || [];
    const childAttrs  = (b.children || []).flatMap(c => c?.customAttributes || []);
    const { front, back } = getPreviewImageUrls([...groupAttrs, ...parentAttrs, ...childAttrs]);

    let inner;
    if (front || back) {
        inner = (
            <div className="odv2-thumb-duo">
                {front && (
                    <div className="odv2-thumb">
                        <img src={front} alt={alt ? `${alt} front` : 'front'} />
                    </div>
                )}
                {back && (
                    <div className="odv2-thumb">
                        <img src={back} alt={alt ? `${alt} back` : 'back'} />
                    </div>
                )}
            </div>
        );
    } else {
        const fallback = b.parent?.image?.url || b.parent?.product?.featuredImage?.url || null;
        inner = <Thumb src={fallback} alt={alt} missing={!fallback} />;
    }

    if (!onPreview) return inner;

    return (
        <button
            type="button"
            className="odv2-thumb-preview-btn"
            onClick={() => onPreview({ b, front, back })}
            aria-label={`Preview ${alt || 'product'}`}
        >
            {inner}
        </button>
    );
}

function Avatar({ name }) {
    const initials = (name || '?').split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase();
    return <span className="odv2-avatar">{initials}</span>;
}

// ── helpers for player extraction ───────────────────────────────────────────
// Customer-facing player attrs are visible on the line item ("Player Name").
// The richer roster metadata (number, team, club, gender, role) is stashed in
// a JSON-encoded blob on the ORDER's customAttributes under `_soccer_order_info`.
// Each line item also carries `_soccer_player_id` so we can join the two.
function getAttr(li, k) {
    const props = li?.customAttributes || li?.properties || [];
    for (const p of props) {
        const key = (p.name || p.key || '').toLowerCase();
        if (key === k.toLowerCase()) return p.value;
    }
    return null;
}

function parseRoster(order) {
    const raw = getAttr(order, '_soccer_order_info');
    if (!raw) return new Map();
    try {
        const list = JSON.parse(raw);
        const m = new Map();
        for (const r of (Array.isArray(list) ? list : [])) {
            if (r && r.id != null) m.set(String(r.id), r);
        }
        return m;
    } catch { return new Map(); }
}

function getPlayer(li, roster) {
    const playerId = getAttr(li, '_soccer_player_id');
    const rosterEntry = (playerId && roster) ? roster.get(String(playerId)) : null;
    const name = getAttr(li, 'Player Name') || getAttr(li, 'Name') || rosterEntry?.name || null;
    const number = (rosterEntry?.config?.number ?? null);
    return {
        playerId,
        name,
        number: number != null ? String(number) : null,
        gender: rosterEntry?.config?.gender ?? null,
        role:   rosterEntry?.role || null,
        club:   rosterEntry?.club?.title || null,
        team:   rosterEntry?.team?.title || null,
    };
}

// ── FO sub-section helpers ───────────────────────────────────────────────────

// Pickup-lifecycle stage of an OPEN pickup FO, from the backend's per-FO
// effective_state: 0 = still at the warehouse, 1 = in transit to the store,
// 2 = ready for pickup, 3 = picked up. Mirrors V1's foIsClosedShipped gate:
// a warehouse-pickup FO that is still open at the warehouse never counts.
function foPickupStage(fo) {
    if (!fo || fo._warehousePickupOpen) return 0;
    // Label-first warehouses: the label print never implies the goods moved (V1).
    if (isLabelFirstWarehouse(fo?.assignedLocation?.location?.id?.split('/').pop())) return 0;
    const ps = fo._pickupState || {};
    if (ps.dest_is_store === false) return 0;
    const st = ps.effective_state;
    return (typeof st === 'number' && st >= 1) ? Math.min(st, 3) : 0;
}

const PICKUP_STAGE_LABELS = { 1: 'In transit', 2: 'Ready for pickup', 3: 'Picked up' };

// Fulfilled from the fulfillment location's perspective: Shopify fulfilled it,
// or the pickup lifecycle moved it past the warehouse (V1 / OpsHub rule).
function isSubSectionDone(subSection) {
    if (!subSection) return false;
    if (subSection.displayMode === 'fulfilled') return true;
    return (subSection.pickupStage || 0) >= 1;
}

// Format an FO sub-section's display status. Returns a human-readable label
// (e.g., "Fulfilled", "Unfulfilled", "In progress").
function formatFoStatus(subSection) {
    if (!subSection) return '';
    if (subSection.displayMode === 'pending' && subSection.pickupStage >= 1) {
        return PICKUP_STAGE_LABELS[subSection.pickupStage] || 'Fulfilled';
    }
    if (subSection.displayMode === 'pending')   return 'Unfulfilled';
    if (subSection.displayMode === 'fulfilled') return 'Fulfilled';
    if (subSection.displayMode === 'refunded')  return 'Refunded';
    // legacy fallback: derive from FO.status
    const s = (subSection.fo?.status || '').toUpperCase();
    if (s === 'CLOSED')      return 'Fulfilled';
    if (s === 'IN_PROGRESS') return 'In progress';
    if (s === 'OPEN')        return 'Unfulfilled';
    return s ? s.charAt(0) + s.slice(1).toLowerCase() : 'Fulfillment';
}

// Pull tracking details off the fulfillment record. Returns
// { carrier, number, url } or null when missing.
function getFoCarrier(subSection) {
    const f = subSection?.fulfillment;
    if (!f) return null;
    // Shopify exposes either `trackingInfo` (array) or top-level fields.
    const ti = (Array.isArray(f.trackingInfo) && f.trackingInfo[0]) || null;
    const number = ti?.number || f.trackingNumber;
    const url    = ti?.url    || f.trackingUrl;
    const carrier = ti?.company || f.trackingCompany || f.service;
    if (!number && !url && !carrier) return null;
    return { carrier: carrier || '', number: number || '', url: url || '' };
}

// FO origin/location label. Shopify exposes assignedLocation on the FO.
function getFoLocationName(subSection) {
    return subSection?.fo?.assignedLocation?.name
        || subSection?.fo?.assignedLocation?.location?.name
        || '';
}

// ── FO presentational components ─────────────────────────────────────────────

function FoStatusPill({ subSection }) {
    const label = formatFoStatus(subSection);
    const mode = subSection?.displayMode || 'legacy';
    const cls = (mode === 'pending' && isSubSectionDone(subSection))
        ? 'odv2-fo-pill odv2-fo-pill--ok'
        : mode === 'pending'
        ? 'odv2-fo-pill odv2-fo-pill--warn'
        : mode === 'fulfilled'
            ? 'odv2-fo-pill odv2-fo-pill--ok'
            : mode === 'refunded'
                ? 'odv2-fo-pill odv2-fo-pill--danger'
                : 'odv2-fo-pill';
    return <span className={cls}>{label}</span>;
}

function FoStatusInline({ subSection, order = null }) {
    if (!subSection) return null;
    const carrier = getFoCarrier(subSection);
    const loc     = getFoLocationName(subSection);
    return (
        <span className="odv2-fo-inline">
            <FoStatusPill subSection={subSection} />
            {loc && <span className="odv2-fo-inline-loc">{loc}</span>}
            {(order?.bulk_shipment_labels || [])
                .filter(l => String(l.fulfillment_order_id) === String((subSection?.fo?.id || '').split('/').pop()))
                .map(l => (
                    l.tracking_url
                        ? <a key={l.id} className="odv2-fo-head-bulk" href={l.tracking_url} target="_blank" rel="noreferrer"
                             title={`Bulk shipment scanned ${new Date(l.scanned_at).toLocaleString()}`}>🚚 {l.tracking_number}</a>
                        : <span key={l.id} className="odv2-fo-head-bulk" title={`Hand-carried box, scanned ${new Date(l.scanned_at).toLocaleString()}`}>🚚 {l.tracking_number}</span>
                ))}
            {carrier && (carrier.number || carrier.url) && (
                <span className="odv2-fo-inline-track">
                    {carrier.carrier && <span>{carrier.carrier} </span>}
                    {carrier.url
                        ? <a href={carrier.url} target="_blank" rel="noreferrer">{carrier.number || 'Tracking'}</a>
                        : <span>{carrier.number}</span>}
                </span>
            )}
        </span>
    );
}

// Rollup of one FO sub-section's bundle dots (virtual fee/protection lines excluded).
function foRollupDots(bundles, portalState, fulfillmentShipped, lineFlags) {
    const real = (bundles || []).filter(b => b?.parent && !isVirtual(b.parent.title));
    if (real.length === 0) return null;
    return rollupStates(real.map(b => dotsForBundle(b.parent, b.children, portalState, fulfillmentShipped, lineFlags)));
}

function FoSubHeaderRow({ subSection, label, open, onToggle, colSpan, order = null, dots = null }) {
    // Per-fulfillment action menu (V1's FulfillmentMenu, same gates): Fulfill &
    // Print Label, Void Label, Change Location, Transfer To Pickup Location,
    // Ready For Pickup, Mark As Picked Up, Cancel Fulfillment (2026-09-15, Eric).
    const menuCtx = useContext(FoMenuContext);
    const fo = subSection?.fo || {};
    const fulfillment = subSection?.fulfillment || null;
    const isOpen = subSection?.displayMode === 'fulfilled' ? false : (fo.status === 'OPEN' || fo.status === 'IN_PROGRESS');
    const pickup = isPickupMethod(fo.deliveryMethod?.methodType);
    const isShipping = (fo.deliveryMethod?.methodType || '').toUpperCase() === 'SHIPPING';
    const pickupLocationName = (menuCtx?.storePickupOverride || '').trim() || fo.deliveryMethod?.presentedName || '';
    const atPickupLocation = pickup && !!pickupLocationName && (fo.assignedLocation?.name || '') === pickupLocationName;
    const isCancelledLabel = !isOpen && fulfillment?.status === 'CANCELLED' && fo.requestStatus === 'UNSUBMITTED';
    const isTrulyFulfilled = !isOpen && !isCancelledLabel;
    const storedLabel = (menuCtx?.storedLabels || []).find(l => {
        if (l.fulfillment_order_id !== fo.id) return false;
        if (subSection?.displayMode === 'pending') return false;
        if (subSection?.displayMode === 'fulfilled' && l.fulfillment_id) return l.fulfillment_id === fulfillment?.id;
        return true;
    }) || null;
    const carrier = getFoCarrier(subSection);
    const loc = getFoLocationName(subSection);
    // V1 parity (2026-09-16): "View / Reprint Shipping & Packing Label" (or
    // Packing Slip when no label was bought through OpsHub). Same gates as V1.
    const { user: _reprintUser } = useAuth();
    const liById = useMemo(() => {
        const m = new Map();
        for (const e of (order?.lineItems?.edges || [])) if (e?.node?.id) m.set(e.node.id, e.node);
        return m;
    }, [order]);
    const hasPhysical = [...(subSection?.lineItemIds || [])].some(id => { const li = liById.get(id); return li && !isVirtual(li.title); });
    const canReprint = !!(menuCtx && fo.id
        && ['admin', 'site_manager', 'user', 'franchise', 'store_manager'].includes(_reprintUser?.role)
        && (storedLabel || isTrulyFulfilled || atPickupLocation || fo._pickupState?.transferred_at)
        && hasPhysical);
    const handleReprint = () => {
        const sk = menuCtx?.storeKey || 'soccerpost';
        let url;
        if (storedLabel) {
            const tail = (storedLabel.shopify_label_gid || '').split('/').pop();
            url = `/api/shipping-labels/${tail}/combined-html?store=${encodeURIComponent(sk)}`;
        } else {
            const qs = new URLSearchParams({ fulfillment_order_id: fo.id, skip_transfer: 'true', store: sk });
            url = `/api/orders/${menuCtx.orderId}/packing-slip-print?${qs}`;
        }
        // Standalone PWA (iPad): navigate the parent window with &return= so
        // the popup's Close button can come back (window.open leaves the PWA).
        const isStandalonePWA = (typeof window !== 'undefined') && (
            window.navigator?.standalone === true
            || (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
        );
        if (isStandalonePWA) {
            const sep = url.includes('?') ? '&' : '?';
            window.location.href = `${url}${sep}return=${encodeURIComponent(window.location.href)}`;
            return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
    };
    const fulfilledAt = subSection?.fulfillment?.createdAt;
    const dateStr = fulfilledAt
        ? new Date(fulfilledAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
        : '';
    return (
        <tr className="odv2-row-fo" data-fo-key={subSection.key}>
            <td colSpan={colSpan - 4}>
                <div className="odv2-fo-head">
                    <span className={`odv2-caret ${open ? 'odv2-caret--open' : ''}`}
                          onClick={onToggle}>▶</span>
                    {label && <span className="odv2-fo-head-label">{label}</span>}
                    <FoStatusPill subSection={subSection} />
                    {loc && <span className="odv2-fo-head-loc">{loc}</span>}
                    {dateStr && <span className="odv2-fo-head-date">{dateStr}</span>}
                    {(order?.bulk_shipment_labels || [])
                        .filter(l => String(l.fulfillment_order_id) === String((subSection?.fo?.id || '').split('/').pop()))
                        .map(l => (
                            l.tracking_url
                                ? <a key={l.id} className="odv2-fo-head-bulk" href={l.tracking_url} target="_blank" rel="noreferrer"
                                     title={`Bulk shipment scanned ${new Date(l.scanned_at).toLocaleString()}`}>🚚 {l.tracking_number}</a>
                                : <span key={l.id} className="odv2-fo-head-bulk" title={`Hand-carried box, scanned ${new Date(l.scanned_at).toLocaleString()}`}>🚚 {l.tracking_number}</span>
                        ))}
                    {carrier && (carrier.number || carrier.url) && (
                        <span className="odv2-fo-head-track">
                            {carrier.carrier && <span>{carrier.carrier} </span>}
                            {carrier.url
                                ? <a href={carrier.url} target="_blank" rel="noreferrer">{carrier.number || 'Tracking'}</a>
                                : <span>{carrier.number}</span>}
                        </span>
                    )}
                    {canReprint && (
                        <button type="button" className="odv2-fo-head-reprint"
                                onClick={e => { e.stopPropagation(); handleReprint(); }}>
                            {storedLabel ? '🖨 View / Reprint Shipping & Packing Label' : '🖨 View / Reprint Packing Slip'}
                        </button>
                    )}
                </div>
            </td>
            {/* Per-fulfillment Picked / Pressed / Packed dots (rollup of the rows
                beneath, which read the backend's per-line flags) — Eric 2026-09-15. */}
            {dots ? <StageDotCells dots={dots} /> : <td colSpan={3} />}
            {/* Fulfillment menu lives right of the dots, in the price column (Eric 2026-09-16) */}
            <td className="odv2-fo-head-menu-cell">
            {menuCtx && fo.id && (
                <span className="odv2-fo-head-menu" onClick={e => e.stopPropagation()}>
                    <FulfillmentMenu
                        fo={fo}
                        fulfillment={fulfillment}
                        orderId={menuCtx.orderId}
                        isOpen={isOpen}
                        isShipping={isShipping}
                        hasLabel={!!storedLabel}
                        pickup={pickup}
                        atPickupLocation={atPickupLocation}
                        pickupReadyAt={fo._pickupState?.ready_for_pickup_at || null}
                        pickedUpAt={fo._pickupState?.picked_up_at || null}
                        isCancelledLabel={isCancelledLabel}
                        isTrulyFulfilled={isTrulyFulfilled}
                        storeKey={menuCtx.storeKey}
                        storeFetch={menuCtx.storeFetch}
                        siblingFos={menuCtx.siblings.filter(sib => sib.id !== fo.id)}
                        storePickupOverride={menuCtx.storePickupOverride}
                        onAfterAction={menuCtx.onAfterAction}
                        onToast={menuCtx.onToast}
                    />
                </span>
            )}
            </td>
        </tr>
    );
}

function VirtualRow({ li, priceOf, fulfilled }) {
    const total = priceOf(li.id);
    return (
        <tr className="odv2-row-virtual-item">
            <td />
            <td>
                <span className="odv2-virtual-title">{li.title}</span>
                {fulfilled === true && <span className="odv2-virt-chip odv2-virt-chip--ok">Fulfilled</span>}
                {fulfilled === false && <span className="odv2-virt-chip odv2-virt-chip--pending">Unfulfilled</span>}
            </td>
            <td><span className="odv2-item-sub">{li.sku || '—'}</span></td>
            <td />
            <td className="odv2-cell-center"><span className="odv2-qty">{li.quantity || 1}</span></td>
            <td colSpan={3} />
            <td className="odv2-cell-right"><span className="odv2-price">${(total || 0).toFixed(2)}</span></td>
        </tr>
    );
}

// ── player grouping ───────────────────────────────────────────────────────────

// Group bundles by player. Items without a player land in an "Unassigned" group.
// Number with a Team Portal override: original struck through, override in red (V1 look).
function PlayerNumber({ number, override, cls }) {
    if (override) {
        const orig = number || override.order_number || '—';
        return (
            <span className="odv2-ov-stack odv2-ov-stack--num">
                <span className={`${cls} odv2-ov-orig`} title="Original number from order">#{orig}</span>
                <span className={`${cls} odv2-ov-new`} title={`Team Portal number override${override.tp_updated_at ? ` (updated ${override.tp_updated_at})` : ''}`}>#{override.override_number}</span>
            </span>
        );
    }
    return number ? <span className={cls}>#{number}</span> : null;
}

function PlayerTeam({ team, override }) {
    if (override) {
        const orig = team || override.order_team || '—';
        return (
            <span className="odv2-ov-stack">
                <span className="odv2-ov-orig" title="Original team from order">{orig}</span>
                <span className="odv2-ov-new" title={`Updated in Team Portal${override.tp_updated_at ? ` ${override.tp_updated_at}` : ''}`}>{override.override_team}</span>
            </span>
        );
    }
    return team ? <span>{team}</span> : null;
}

function groupByPlayer(bundles, roster, tpOv = null) {
    const groups = new Map();
    for (const b of bundles) {
        const p = getPlayer(b.parent, roster);
        const key = p.playerId || p.name || '__unassigned__';
        if (!groups.has(key)) {
            // Team Portal overrides (V1 parity, Eric 2026-09-16): the order's
            // number / team are struck through and the TP value shown in red.
            const ov = tpOv ? tpOv.byPlayer(p.playerId, p.name) : { number: null, team: null };
            groups.set(key, {
                key,
                name: p.name || 'Unassigned items',
                number: p.number,
                club: p.club,
                team: p.team,
                numberOverride: ov.number,
                teamOverride: ov.team,
                playerId: p.playerId,
                gender: p.gender,
                role: p.role,
                bundles: [],
            });
        }
        groups.get(key).bundles.push(b);
    }
    return Array.from(groups.values()).sort((a, b) => {
        if (a.key === '__unassigned__') return 1;
        if (b.key === '__unassigned__') return -1;
        return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    });
}

// Split bundles into three buckets:
//  - playerBundles: physical items attached to a player (via _soccer_player_id
//    or "Player Name" attr / roster lookup)
//  - retailBundles: physical items with no player attribution (e.g. counter
//    sales, pure retail merch)
//  - virtualLineItems: line items matching isVirtual() (Free Returns +
//    Package Protection, Processing Fee). These are always single-line
//    "bundles" produced by buildBundles, so we extract their .parent.
function splitBundlesByCategory(bundles, roster) {
    const playerBundles = [];
    const retailBundles = [];
    const virtualLineItems = [];
    for (const b of bundles) {
        const parent = b.parent;
        if (parent && isVirtual(parent.title)) {
            virtualLineItems.push(parent);
            continue;
        }
        const p = getPlayer(parent, roster);
        // Require a *resolved* player name to count as a player row — items
        // with a stale _soccer_player_id but no Player Name attr / roster
        // hit fall through to Retail Items.
        if (p.name) playerBundles.push(b);
        else retailBundles.push(b);
    }
    return { playerBundles, retailBundles, virtualLineItems };
}

// Build per-Fulfillment "sub-sections" for an order. Returns an array of:
//   { key, fo, fulfillment, displayMode, lineItemIds: Set<gid>, sortRank, sortTs }
//
// displayMode: 'pending' (Unfulfilled, has remainingQuantity > 0)
//              'fulfilled' (Fulfilled — one entry per non-cancelled
//                           fulfillment edge on the FO, ordered by createdAt)
//              'legacy' (FO with no fulfilled or pending — fallback)
//
// Sort: pending first (rank 0), then fulfilled in createdAt-asc (rank 1).
// NONE-method internal-transfer FOs are included so virtual line items
// (Processing Fee, Free Returns, Package Protection) get a real header
// row on the Fulfillments tab — they just naturally lack tracking details.
function buildFoSubSections(order) {
    const foEdges = order?.fulfillmentOrders?.edges || [];
    const fulfillmentsList = order?.fulfillments || [];
    const out = [];
    for (const edge of foEdges) {
        const fo = edge?.node;
        if (!fo) continue;
        const before = out.length;

        // 1) One entry per non-cancelled fulfillment, createdAt-asc
        const ownIds = (fo.fulfillments?.edges || [])
            .map(e => e.node?.id)
            .filter(Boolean);
        const ownFulfs = ownIds
            .map(id => fulfillmentsList.find(f => f.id === id))
            .filter(f => f && (f.status || '').toUpperCase() !== 'CANCELLED')
            .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));

        // Skip stale CLOSED FOs left behind by split-transfers or voided labels:
        // CLOSED + non-cancelled order + non-pickup + no surviving fulfillments
        // → inventory moved out to another FO; rendering a legacy card for it
        // shows a phantom fulfillment that "no longer exists".
        if (fo.status === 'CLOSED' && !order?.cancelledAt && ownFulfs.length === 0) {
            const dm = (fo.deliveryMethod?.methodType || '').toUpperCase();
            if (dm !== 'PICK_UP') continue;
        }

        for (const f of ownFulfs) {
            const ids = new Set();
            for (const e of (f.fulfillmentLineItems?.edges || [])) {
                const id = e.node?.lineItem?.id;
                if (id) ids.add(id);
            }
            out.push({
                key: `${fo.id}#${f.id}`,
                fo, fulfillment: f,
                displayMode: 'fulfilled',
                lineItemIds: ids,
                sortRank: 1,
                sortTs: new Date(f.createdAt || 0).getTime(),
            });
        }

        // 2) Pending entry (remainingQuantity > 0)
        const foIsOpen = fo.status === 'OPEN' || fo.status === 'IN_PROGRESS';
        if (foIsOpen) {
            const ids = new Set();
            for (const e of (fo.lineItems?.edges || [])) {
                if ((e.node?.remainingQuantity || 0) > 0) {
                    const id = e.node?.lineItem?.id;
                    if (id) ids.add(id);
                }
            }
            if (ids.size > 0) {
                out.push({
                    key: `${fo.id}#pending`,
                    fo, fulfillment: null,
                    displayMode: 'pending',
                    // V1 rule (foIsClosedShipped): a pickup FO the warehouse has
                    // already handed off — transferred to the store (1), marked
                    // ready (2) or picked up (3) — is fulfilled from the
                    // fulfillment location's perspective even while Shopify still
                    // says OPEN. effective_state comes from the backend
                    // (compute_fo_effective_state); dest_is_store guards
                    // fulfillment→fulfillment moves. Eric 2026-09-15.
                    pickupStage: foPickupStage(fo),
                    lineItemIds: ids,
                    sortRank: 0,
                    sortTs: 0,
                });
            }
        }

        // 2b) Sweep up FO line items that are "fulfilled" per the FO
        //     (remainingQuantity === 0) but missing from any fulfillment's
        //     fulfillmentLineItems — common after edits/refunds. Attach them
        //     to the FO's most recent SUCCESS sub-section so the bundle still
        //     shows under a real fulfillment instead of an orphan "no-fo" row.
        const foEntries = out.slice(before);
        const successEntries = foEntries.filter(e => e.displayMode === 'fulfilled');
        if (successEntries.length > 0) {
            const covered = new Set();
            for (const e of foEntries) {
                e.lineItemIds.forEach(id => covered.add(id));
            }
            const mostRecent = successEntries
                .slice()
                .sort((a, b) => b.sortTs - a.sortTs)[0];
            for (const e of (fo.lineItems?.edges || [])) {
                const liNode = e.node;
                const liId = liNode?.lineItem?.id;
                if (!liId) continue;
                if (covered.has(liId)) continue;
                if ((liNode.remainingQuantity || 0) > 0) continue; // pending already handled above
                if ((liNode.totalQuantity || 0) <= 0) continue;
                mostRecent.lineItemIds.add(liId);
            }
        }

        // 3) Fallback: FO produced nothing
        if (out.length === before) {
            const ids = new Set();
            for (const e of (fo.lineItems?.edges || [])) {
                const id = e.node?.lineItem?.id;
                if (id) ids.add(id);
            }
            out.push({
                key: fo.id,
                fo, fulfillment: null,
                displayMode: 'legacy',
                lineItemIds: ids,
                sortRank: 2,
                sortTs: 0,
            });
        }
    }
    out.sort((a, b) => a.sortRank - b.sortRank || a.sortTs - b.sortTs);
    return out;
}

// Given a flat list of bundles and the order-wide FO sub-sections,
// return [{ subSection, bundles }] in the same order as subSections.
// Bundles that don't match any subSection (orphans — should be rare)
// fall through to a synthetic "no-fo" sub-section at the end.
// V1's per-FO "Sort by": 'order' keeps Shopify line order, 'product' sorts by
// title then SKU, 'player' by roster name (unassigned last) then title.
function sortFoBundles(list, mode, roster) {
    if (!mode || mode === 'order') return list;
    const byTitle = (a, b) =>
        (a.parent?.title || '').localeCompare(b.parent?.title || '', undefined, { sensitivity: 'base' })
        || (a.parent?.sku || '').localeCompare(b.parent?.sku || '');
    const arr = list.slice();
    if (mode === 'product') return arr.sort(byTitle);
    return arr.sort((a, b) => {
        const pa = getPlayer(a.parent, roster).name || '\uffff';
        const pb = getPlayer(b.parent, roster).name || '\uffff';
        return pa.localeCompare(pb, undefined, { sensitivity: 'base' }) || byTitle(a, b);
    });
}

function groupBundlesBySubSection(bundles, subSections) {
    const out = subSections.map(s => ({ subSection: s, bundles: [] }));
    const orphan = [];
    for (const b of bundles) {
        const parentId = b.parent?.id;
        if (!parentId) { orphan.push(b); continue; }
        let matched = false;
        for (const row of out) {
            if (row.subSection.lineItemIds.has(parentId)) {
                row.bundles.push(b);
                matched = true;
                break;
            }
        }
        if (!matched) orphan.push(b);
    }
    const filtered = out.filter(row => row.bundles.length > 0);
    if (orphan.length > 0) {
        filtered.push({
            subSection: { key: 'no-fo', displayMode: 'legacy', fo: null, fulfillment: null, lineItemIds: new Set() },
            bundles: orphan,
        });
    }
    return filtered;
}

// ── TagsCard — sidebar collapsible card grouping order.tags into buckets ───
//
// Known-prefix tags ("Started <ts>", "Finished <ts>", "PickedSku <sku>",
// "NotPicked <sku>", "Partially Picked By <who>") collapse into a single
// bucket chip showing the name + count. Plain tags render as standalone chips.
// Click a bucket chip to expand a panel below with the individual values.
const KNOWN_BUCKETS = [
    // Longest prefix first so "Partially Picked By " wins over hypothetical
    // "Partially" matches.
    { prefix: 'Partially Picked By ', name: 'Partially Picked' },
    { prefix: 'Started ',    name: 'Started'    },
    { prefix: 'Finished ',   name: 'Finished'   },
    { prefix: 'PickedSku ',  name: 'PickedSku'  },
    { prefix: 'NotPicked ',  name: 'NotPicked'  },
];

function groupTagsForDisplay(tags) {
    const bucketMap = new Map();
    KNOWN_BUCKETS.forEach(b => bucketMap.set(b.name, { ...b, values: [] }));
    const singletons = [];
    tags.forEach(tag => {
        const match = KNOWN_BUCKETS.find(b => tag.startsWith(b.prefix));
        if (match) bucketMap.get(match.name).values.push(tag.slice(match.prefix.length));
        else singletons.push(tag);
    });
    const buckets = Array.from(bucketMap.values()).filter(b => b.values.length > 0);
    return { buckets, singletons };
}

function TagsCard({ tags }) {
    // Card default expanded — buckets keep the chip wall short.
    const [open, setOpen] = useState(true);
    const [openBuckets, setOpenBuckets] = useState({});
    const { buckets, singletons } = useMemo(() => groupTagsForDisplay(tags), [tags]);
    const toggleBucket = (name) => setOpenBuckets(s => ({ ...s, [name]: !s[name] }));
    return (
        <div className="odv2-card odv2-tags-card">
            <button className="odv2-card-disclosure"
                    aria-expanded={open}
                    onClick={() => setOpen(o => !o)}>
                <span className="odv2-card-title odv2-card-title--button">Tags <span className="odv2-card-count">({tags.length})</span></span>
                <span className={`odv2-disclosure-caret ${open ? 'odv2-disclosure-caret--open' : ''}`}>▾</span>
            </button>
            <Collapsible open={open} id="odv2-tags-collapse"
                         transition={{ duration: '150ms', timingFunction: 'ease-out' }}>
                <Box paddingBlockStart="300">
                    <BlockStack gap="300">
                        <InlineStack gap="200" wrap>
                            {buckets.map(b => (
                                <Tag key={b.name} onClick={() => toggleBucket(b.name)}>
                                    <span className="odv2-tag-bucket-label">
                                        {b.name} <span className="odv2-tag-bucket-count">{b.values.length}</span>
                                    </span>
                                </Tag>
                            ))}
                            {singletons.map(t => (
                                <Tag key={t}>{t}</Tag>
                            ))}
                        </InlineStack>
                        {buckets.map(b => (
                            <Collapsible key={`${b.name}-panel`}
                                         open={!!openBuckets[b.name]}
                                         id={`odv2-tag-bucket-${b.name}`}
                                         transition={{ duration: '150ms', timingFunction: 'ease-out' }}>
                                <Box background="bg-surface-secondary"
                                     padding="300"
                                     borderRadius="200"
                                     borderColor="border"
                                     borderWidth="025">
                                    <BlockStack gap="200">
                                        <Text variant="bodySm" fontWeight="semibold" as="p">{b.name}</Text>
                                        <InlineStack gap="150" wrap>
                                            {b.values.map((v, i) => (
                                                <Tag key={`${b.name}-${i}`}>{v}</Tag>
                                            ))}
                                        </InlineStack>
                                    </BlockStack>
                                </Box>
                            </Collapsible>
                        ))}
                    </BlockStack>
                </Box>
            </Collapsible>
        </div>
    );
}

// ── OrderSummarySection — V1-parity Subtotal/Discount/Shipping/Tax/Total/Paid/Refunded ─
function OrderSummarySection({ order, lineItems }) {
    const moneyOf = (set) => {
        const v = set?.shopMoney?.amount ?? set?.amount ?? set;
        const n = parseFloat(v);
        return isFinite(n) ? n : 0;
    };
    const fmt = (set) => `$${moneyOf(set).toFixed(2)}`;
    const num = moneyOf;

    // Match Shopify admin "Subtotal" — gross items total BEFORE any discount.
    // order.subtotalPriceSet reports the after-line-item-discount total, which
    // becomes $0 when a discount zeroes each LI (e.g. PACKAGES DISCOUNT).
    const subtotalGross = (lineItems || []).reduce((s, li) => {
        const ot = parseFloat(li.originalTotalSet?.shopMoney?.amount);
        if (isFinite(ot)) return s + ot;
        const up = parseFloat(li.originalUnitPriceSet?.shopMoney?.amount) || 0;
        const qty = parseInt(li.quantity || 0, 10) || 0;
        return s + up * qty;
    }, 0);
    const fmtNum   = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;
    const tax      = num(order.totalTaxSet);
    const ship     = num(order.totalShippingPriceSet);
    const discount = num(order.currentTotalDiscountsSet);
    const refunded = num(order.totalRefundedSet);
    const received = num(order.totalReceivedSet);
    const taxLines     = order.taxLines || [];
    const discountApps = (order.discountApplications?.edges || []).map(e => e.node);
    const totalItems   = lineItems.reduce((s, li) => s + (parseInt(li.currentQuantity ?? li.quantity ?? 0, 10) || 0), 0);

    const fos = (order.fulfillmentOrders?.edges || []).map(e => e.node);
    const firstPickupFO = fos.find(f => (f.deliveryMethod?.methodType || '').toUpperCase() === 'PICK_UP');
    const overrideLoc = (order.store_pickup_override || '').trim() || null;
    const isPickup = !!firstPickupFO || !!overrideLoc;
    const pickupLocationName = overrideLoc
        || firstPickupFO?.deliveryMethod?.presentedName
        || firstPickupFO?.assignedLocation?.name
        || '';

    const fs = order.displayFinancialStatus || '';
    const fsLabel = fs.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    const fsTone = ({
        PAID: { bg: '#e7f5ec', fg: '#0a6a1f', border: '#b6dfc1' },
        PARTIALLY_REFUNDED: { bg: '#fef3c7', fg: '#92400e', border: '#fde68a' },
        REFUNDED: { bg: '#fee2e2', fg: '#991b1b', border: '#fecaca' },
        PARTIALLY_PAID: { bg: '#fef3c7', fg: '#92400e', border: '#fde68a' },
        PENDING: { bg: '#fef3c7', fg: '#92400e', border: '#fde68a' },
        VOIDED: { bg: '#f3f4f6', fg: '#374151', border: '#d1d5db' },
    })[fs] || { bg: '#e7f5ec', fg: '#0a6a1f', border: '#b6dfc1' };

    return (
        <div className="odv2-order-summary">
            <div className="odv2-order-summary-head">
                <h3 className="odv2-order-summary-title">Order Summary <span className="odv2-order-summary-count">({totalItems} {totalItems === 1 ? 'item' : 'items'})</span></h3>
                {fs && (
                    <span className="odv2-order-summary-fs" style={{
                        color: fsTone.fg, backgroundColor: fsTone.bg, borderColor: fsTone.border,
                    }}>{fsLabel}</span>
                )}
            </div>
            <div className="od-totals">
                <div className="od-totals-row">
                    <span className="od-totals-label">Subtotal</span>
                    <span className="od-totals-mid">{totalItems} {totalItems === 1 ? 'item' : 'items'}</span>
                    <span>{fmtNum(subtotalGross)}</span>
                </div>
                {discount > 0 && (
                    <div className="od-totals-row">
                        <span className="od-totals-label">Discount</span>
                        <span className="od-totals-mid">
                            {discountApps.map((d, i) => (
                                <span key={i} style={{ marginRight: 8 }}>{d.code || d.title || '—'}</span>
                            ))}
                        </span>
                        <span>−{fmt(order.currentTotalDiscountsSet)}</span>
                    </div>
                )}
                {isPickup ? (
                    <div className="od-totals-row">
                        <span className="od-totals-label">Pickup</span>
                        <span className="od-totals-mid">{pickupLocationName}</span>
                        <span>{fmt(order.totalShippingPriceSet)}</span>
                    </div>
                ) : (
                    <div className="od-totals-row">
                        <span className="od-totals-label">Shipping</span>
                        <span className="od-totals-mid">{order.shippingLine?.title || ''}</span>
                        <span>{fmt(order.totalShippingPriceSet)}</span>
                    </div>
                )}
                {taxLines.length > 0 ? taxLines.map((tl, i) => (
                    <div key={i} className="od-totals-row">
                        <span className="od-totals-label">{i === 0 ? 'Taxes' : ''}</span>
                        <span className="od-totals-mid">{tl.title} {tl.ratePercentage != null ? `${tl.ratePercentage}%` : ''}</span>
                        <span>{fmt(tl.priceSet)}</span>
                    </div>
                )) : (tax > 0 && (
                    <div className="od-totals-row">
                        <span className="od-totals-label">Taxes</span>
                        <span className="od-totals-mid"></span>
                        <span>{fmt(order.totalTaxSet)}</span>
                    </div>
                ))}
                <div className="od-totals-row od-totals-grand">
                    <span>Total</span>
                    <span className="od-totals-mid"></span>
                    <span>{fmt(order.totalPriceSet)}</span>
                </div>
            </div>
            {(received > 0 || refunded > 0) && (
                <div className="od-totals" style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e5e7eb' }}>
                    {received > 0 && (
                        <div className="od-totals-row">
                            <span className="od-totals-label">Paid</span>
                            <span className="od-totals-mid"></span>
                            <span>{fmt(order.totalReceivedSet)}</span>
                        </div>
                    )}
                    {refunded > 0 && (
                        <div className="od-totals-row" style={{ color: '#991b1b' }}>
                            <span className="od-totals-label">Refunded</span>
                            <span className="od-totals-mid"></span>
                            <span>−{fmt(order.totalRefundedSet)}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ── workflow rail (sidebar) ─────────────────────────────────────────────────
function WorkflowRail({ totals: pickPressTotals, createdAt, fulfillBy, printed = false }) {
    // 2026-09-15 (Eric): "(1) Order Printed" leads the rail; pick / press / pack shift to 2-4.
    const totals = useMemo(() => ({
        printed: [printed ? 1 : 0, 1],
        picked:  [pickPressTotals.pickedNum,        pickPressTotals.pickedDen],
        pressed: [pickPressTotals.pressedBundles,   pickPressTotals.pressableBundles],
        packed:  [pickPressTotals.packedNum,        pickPressTotals.packedDen],
    }), [pickPressTotals, printed]);

    const stageMeta = [
        { key: 'printed', num: 1, label: 'Order Printed' },
        { key: 'picked',  num: 2, label: 'Picked' },
        { key: 'pressed', num: 3, label: 'Pressed' },
        { key: 'packed',  num: 4, label: 'Packed' },
    ];

    const fmtAnchor = (d) => d
        ? d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
        : '—';

    return (
        <div className="odv2-card">
            <h3 className="odv2-card-title">Workflow</h3>
            <div className="odv2-rail-anchor odv2-rail-anchor--top">
                <span className="odv2-rail-anchor-dot" aria-hidden>●</span>
                <div className="odv2-rail-anchor-text">
                    <div className="odv2-rail-anchor-label">Order placed</div>
                    <div className="odv2-rail-anchor-time">{fmtAnchor(createdAt)}</div>
                </div>
            </div>
            {stageMeta.map(({ key, num, label }) => {
                const [done, total] = totals[key];
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                const isDone = total > 0 && done >= total;
                const isActive = !isDone && done > 0;
                return (
                    <div key={key} className="odv2-rail-stage">
                        <div className="odv2-rail-head">
                            <span className={`odv2-rail-num ${isDone ? 'odv2-rail-num--done' : isActive ? 'odv2-rail-num--active' : ''}`}>
                                {isDone ? '✓' : num}
                            </span>
                            <span className="odv2-rail-name">{label}</span>
                            <span className="odv2-rail-count">{key === 'printed' ? (isDone ? 'Printed' : 'Not printed') : `${done} / ${total}`}</span>
                        </div>
                        <div className="odv2-rail-bar">
                            <div className={`odv2-rail-bar-fill odv2-rail-bar-fill--${key}`} style={{ width: `${pct}%` }} />
                        </div>
                    </div>
                );
            })}
            <div className="odv2-rail-anchor odv2-rail-anchor--bot">
                <span className="odv2-rail-anchor-dot" aria-hidden>◆</span>
                <div className="odv2-rail-anchor-text">
                    <div className="odv2-rail-anchor-label">Fulfill by</div>
                    <div className="odv2-rail-anchor-time">{fmtAnchor(fulfillBy)}</div>
                </div>
            </div>
        </div>
    );
}

// ── Order Info (V1 parity, 2026-09-15 Eric): CRM, Expected Fulfillment
// Location and the Team Portal fulfillment window, read from the same order
// attributes V1 uses. Renders nothing when the order carries none of them. ──
function OrderInfoCard({ order }) {
    const attrs = order?.customAttributes || [];
    const expectedLoc = attrs.find(a => a.key === '_soccer_fulfillment_location')?.value || '';
    let fulfillmentTime = '';
    try {
        const clubRaw = attrs.find(a => a.key === '_soccer_club')?.value;
        const snap = clubRaw ? (JSON.parse(clubRaw)?.fulfillment_property?.value || '') : '';
        const nums = String(snap).match(/\d+/g) || [];
        if (nums.length >= 2) fulfillmentTime = `${nums[0]}-${nums[1]} Business Days`;
        else if (nums.length === 1) fulfillmentTime = `${nums[0]} Business Days`;
    } catch (_) { /* malformed attr → no row */ }
    const crm = order?.teamportal_crm || '';
    if (!crm && !expectedLoc && !fulfillmentTime) return null;
    const row = (label, value) => (
        <div className="odv2-info-row">
            <span className="odv2-info-label">{label}</span>
            <span className="odv2-info-value">{value}</span>
        </div>
    );
    return (
        <div className="odv2-card odv2-info-card">
            <h3 className="odv2-card-title">Order Info</h3>
            {crm && row('CRM', crm)}
            {expectedLoc && row('Expected Fulfillment Location', expectedLoc)}
            {fulfillmentTime && row('Fulfillment Time', fulfillmentTime)}
        </div>
    );
}

// ── Modify-mode action bar row (rendered as the first <tr> in each table's
// <thead> so the select-all dots line up with the Picked / Pressed columns) ─
function ModifyBarRow({ controls }) {
    if (!controls) return null;
    const {
        pendingChanges, pendingSubmitting,
        cancelModify, applyPending,
        selectAllTargets, bigPickedState, bigPressedState,
        onSelectAllStage,
    } = controls;
    const n = pendingChanges.size;
    const pickedDisabled = pendingSubmitting || !selectAllTargets.picked.length;
    const pressedDisabled = pendingSubmitting || !selectAllTargets.pressed.length;
    return (
        <tr className="odv2-modify-bar-row">
            <td colSpan={5} className="odv2-modify-bar-cell odv2-modify-bar-cell--label">
                <div className="odv2-modify-bar-inner">
                    <span className="odv2-modify-bar__label">Modify Picked &amp; Pressed</span>
                    <span className="odv2-modify-bar__hint">
                        Click the picked/pressed dots to stage changes
                        {n > 0 && (<> · <strong>{n}</strong> pending</>)}
                    </span>
                    <span className="odv2-toolbar-spacer" />
                    <button type="button" className="odv2-segctl-btn"
                            onClick={cancelModify} disabled={pendingSubmitting}>Cancel</button>
                    <button type="button" className="odv2-segctl-btn odv2-segctl-btn--primary"
                            onClick={applyPending}
                            disabled={n === 0 || pendingSubmitting}>
                        {pendingSubmitting ? 'Applying…' : `Apply${n ? ` (${n})` : ''}`}
                    </button>
                </div>
            </td>
            <td className="odv2-modify-bar-cell odv2-modify-bar-cell--stage">
                <button type="button"
                        className={`odv2-bigdot odv2-bigdot--${bigPickedState}`}
                        onClick={() => onSelectAllStage('picked', selectAllTargets.picked)}
                        disabled={pickedDisabled}
                        title={bigPickedState === 'done' ? 'Mark all NOT picked' : 'Mark all picked'}
                        aria-label="Select all picked" />
            </td>
            <td className="odv2-modify-bar-cell odv2-modify-bar-cell--stage">
                <button type="button"
                        className={`odv2-bigdot odv2-bigdot--${bigPressedState}`}
                        onClick={() => onSelectAllStage('pressed', selectAllTargets.pressed)}
                        disabled={pressedDisabled}
                        title={bigPressedState === 'done' ? 'Mark all NOT pressed' : 'Mark all pressed'}
                        aria-label="Select all pressed" />
            </td>
            <td className="odv2-modify-bar-cell" />
            <td className="odv2-modify-bar-cell" />
        </tr>
    );
}

// ── bundle + deco row renderer (extracted helper) ───────────────────────────
// The FO's Shopify location id (numeric) — what the pick / press mark
// endpoints need as shopify_location_id.
function foLocIdOf(subSection) {
    return String(subSection?.fo?.assignedLocation?.location?.id || '').split('/').pop() || '';
}

function renderBundleAndDecos({ b, bundleKey, openBundles, toggleBundle, portalState, fulfillmentShipped, priceOf, bundleDotsForB, modifyMode, pendingChanges, onTogglePending, splitBundleMap = {}, npCtx = {}, lineFlags = null, tpReq = null, tpOv = null, foLocId = '' }) {
    const bundleOpen = openBundles.has(bundleKey);
    const dots = bundleDotsForB;
    const parentGk = b.parent.sku;
    // Press is only meaningful when the parent has decoration children
    // (i.e., it's a real bundle, not a standalone item like a hat). Mirrors
    // dotStates.bundleKind(parent, children) === 'bundle'.
    const hasDecoChildren = (b.children || []).some(c => isDecoration(c));
    const parentClickable = modifyMode && !!parentGk
        ? { picked: true, pressed: hasDecoChildren, packed: false }
        : null;
    const parentPending = modifyMode && !!parentGk
        ? {
            picked:  pendingChanges?.has(`${parentGk}::picked`),
            pressed: pendingChanges?.has(`${parentGk}::pressed`),
            packed:  false,
        }
        : null;
    const onParentToggle = (stage) => {
        if (!parentGk) return;
        onTogglePending?.({
            groupKey: parentGk,
            stage,
            qty: b.parent.quantity || 1,
            currentState: dots[stage].state,
            // 2026-09-20 (Mike Mara, SP order 7244443943090): the backend
            // requires the FO's shopify_location_id for pick / press marks
            // (HTTP 400 without it); V1 has always sent it.
            locId: foLocId,
        });
    };
    const decoChildren = b.children.filter(c => isDecoration(c));
    const totalPrice = (priceOf(b.parent.id) || 0)
        + b.children.reduce((s, c) => s + (priceOf(c.id) || 0), 0);
    const variant = b.parent.variant?.title && b.parent.variant.title !== 'Default Title'
        ? b.parent.variant.title : (b.parent.variantTitle || '');
    const returnable = getAttr(b.parent, 'Returnable');

    return (
        <React.Fragment key={bundleKey}>
            <tr className="odv2-row-bundle">
                <td>
                    {decoChildren.length > 0 && (
                        <span className={`odv2-caret ${bundleOpen ? 'odv2-caret--open' : ''}`}
                              onClick={() => toggleBundle(bundleKey)}>▶</span>
                    )}
                </td>
                <td>
                    <div className="odv2-item-cell">
                        {/* Team Portal Required / Default / Recommended / Off-Spec badge sits
                            under the image, like V1 (Eric 2026-09-15). */}
                        <div className="odv2-thumb-col">
                            <BundleParentThumb b={b} alt={b.parent.title} />
                            {tpReq?.[b.parent.id] && <span className="odv2-thumb-badge"><TpReqBadge entry={tpReq[b.parent.id]} /></span>}
                        </div>
                        <div className="odv2-item-text">
                            <div className="odv2-item-title">
                                <ProductLink li={b.parent}>{b.parent.title}</ProductLink>
                                {(splitBundleMap[b.parent.lineItemGroup?.id] ?? splitBundleMap[b.parent.id]) != null && (
                                    <JumbleBadge color={splitBundleMap[b.parent.lineItemGroup?.id] ?? splitBundleMap[b.parent.id]} />
                                )}
                                {decoChildren.length > 0 && (
                                    <span className="odv2-item-deco-count">+{decoChildren.length}</span>
                                )}
                            </div>
                            {/* Return policy pill on its own line under the title (Eric 2026-09-15) */}
                            {returnable && (returnable.toLowerCase() === 'no' || returnable.toLowerCase().includes('exchange')) && (
                                <div className="odv2-item-policy">
                                    {returnable.toLowerCase() === 'no'
                                        ? <span className="odv2-pill-inline odv2-pill-inline--danger">Final sale</span>
                                        : <span className="odv2-pill-inline odv2-pill-inline--amber">Exchange only</span>}
                                </div>
                            )}
                        </div>
                    </div>
                </td>
                <td><span className="odv2-item-sub">{b.parent.sku || '—'}</span><SkuNotificationCell sku={b.parent.sku} /></td>
                <td><span className="odv2-variant">{variant}</span></td>
                <td className="odv2-cell-center"><span className="odv2-qty">{b.parent.quantity || 1}</span></td>
                <StageDotCells dots={dots}
                               clickable={parentClickable}
                               pending={parentPending}
                               onToggle={onParentToggle} />
                <td className="odv2-cell-right"><span className="odv2-price">${totalPrice.toFixed(2)}</span></td>
            </tr>

            {bundleOpen && decoChildren.map((c, ci) => {
                const cps = psForSku(portalState, c.sku) || (portalState[c.sku] || null);
                // Picked: the embellishment's own line (BE flag wins). Pressed +
                // Packed: an embellishment is pressed onto / packed with its
                // parent garment, so it inherits the bundle's state (Eric, 2026-09-15).
                const cdots = dotsForRow('decoration', c, cps, fulfillmentShipped, lineFlags?.[c.id]);
                cdots.pressed = dots.pressed;
                cdots.packed  = dots.packed;
                const dvariant = c.variant?.title && c.variant.title !== 'Default Title' ? c.variant.title : '';
                const dprice = priceOf(c.id) || 0;
                const cgk = c.sku;
                const cClickable = modifyMode && !!cgk
                    ? { picked: true, pressed: false, packed: false }
                    : null;
                const cPending = modifyMode && !!cgk
                    ? { picked: pendingChanges?.has(`${cgk}::picked`), pressed: false, packed: false }
                    : null;
                const onCToggle = (stage) => {
                    if (!cgk) return;
                    onTogglePending?.({
                        groupKey: cgk,
                        stage,
                        qty: c.quantity || 1,
                        currentState: cdots[stage].state,
                    });
                };
                // On-screen number preview (V1 rule): a child product with a
                // player_number_preview metaobject + a printed number renders the
                // rendered number instead of the catalog thumbnail.
                const cPreview = getPreviewFor(c, npCtx);
                const cNum = (tpOv && tpOv.numberFor(b.parent))
                    || getPrintedNumberAttr(c.customAttributes)
                    || getPrintedNumberAttr(b.parent?.customAttributes)
                    || cPreview?.defaultValue || '';
                const cJumble = splitBundleMap[c.lineItemGroup?.id] ?? splitBundleMap[c.id];
                return (
                    <tr className="odv2-row-deco" key={`${bundleKey}::${c.id}::${ci}`}>
                        <td />
                        <td>
                            <div className="odv2-item-cell">
                                {cPreview && cNum
                                    ? <NumberPreviewThumb preview={cPreview} number={cNum} size={40} borderColor="#e5e7eb" />
                                    : <Thumb src={c.image?.url || c.product?.featuredImage?.url}
                                             alt={c.title} missing={!c.image?.url && !c.product?.featuredImage?.url} />}
                                <div className="odv2-item-text">
                                    <div className="odv2-item-title odv2-item-title--deco">
                                        <ProductLink li={c}>{c.title}</ProductLink>
                                        {cJumble != null && <JumbleBadge color={cJumble} />}
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td><span className="odv2-item-sub">{c.sku || '—'}</span><SkuNotificationCell sku={c.sku} /></td>
                        <td><span className="odv2-variant">{dvariant}</span></td>
                        <td className="odv2-cell-center"><span className="odv2-qty">{c.quantity || 1}</span></td>
                        <StageDotCells dots={cdots}
                                       clickable={cClickable}
                                       pending={cPending}
                                       onToggle={onCToggle} />
                        <td className="odv2-cell-right"><span className="odv2-price">${dprice.toFixed(2)}</span></td>
                    </tr>
                );
            })}
        </React.Fragment>
    );
}

// ── items tree (player → bundle → decoration) ──────────────────────────────
function ItemsTree({ bundles, portalState, fulfillmentShipped, shippedLineIds, priceOf, density, roster, subSections, expandAllNonce, expandPlayersNonce, compressAllNonce, onUserToggle, modifyMode, pendingChanges, onTogglePending, modifyBar, order = null }) {
    const lineFlags = useContext(LineFlagsContext);
    const tpReq = useContext(TpReqContext);
    const splitBundleMap = useContext(JumbleContext);
    const npCtx = useContext(NpContext);
    const { playerBundles, retailBundles, virtualLineItems } = useMemo(
        () => splitBundlesByCategory(bundles, roster),
        [bundles, roster]
    );
    const tpOv = useContext(TpOverridesContext);
    const playerGroups = useMemo(
        () => groupByPlayer(playerBundles, roster, tpOv),
        [playerBundles, roster, tpOv]
    );

    const [openPlayers, setOpenPlayers] = useState(() => new Set(playerGroups.map(p => p.key)));
    const [openBundles, setOpenBundles] = useState(() => new Set());
    const [openFos, setOpenFos] = useState(
        () => new Set(Array.isArray(subSections) ? subSections.map(s => s.key) : [])
    );
    const [openRetail, setOpenRetail] = useState(true);
    const [openVirtual, setOpenVirtual] = useState(true);

    useEffect(() => {
        if (!Array.isArray(subSections)) return;
        setOpenFos(new Set(subSections.map(s => s.key)));
    }, [subSections]);

    useEffect(() => {
        if (!expandAllNonce) return;
        setOpenPlayers(new Set(playerGroups.map(p => p.key)));
        setOpenFos(new Set(Array.isArray(subSections) ? subSections.map(s => s.key) : []));
        setOpenRetail(true);
        setOpenVirtual(true);
        const allBundleKeys = new Set();
        playerGroups.forEach(pg => {
            const subGroups = groupBundlesBySubSection(pg.bundles, subSections || []);
            subGroups.forEach(({ subSection, bundles: subBundles }) => {
                subBundles.forEach((b, bi) => {
                    allBundleKeys.add(`${pg.key}::${subSection.key}::${b.parent.id}::${bi}`);
                });
            });
        });
        const retailSubGroups = groupBundlesBySubSection(retailBundles, subSections || []);
        retailSubGroups.forEach(({ subSection, bundles: subBundles }) => {
            subBundles.forEach((b, bi) => {
                allBundleKeys.add(`__retail__::${subSection.key}::${b.parent.id}::${bi}`);
            });
        });
        setOpenBundles(allBundleKeys);
    }, [expandAllNonce]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!compressAllNonce) return;
        setOpenPlayers(new Set());
        setOpenFos(new Set());
        setOpenRetail(false);
        setOpenVirtual(false);
        setOpenBundles(new Set());
    }, [compressAllNonce]); // eslint-disable-line react-hooks/exhaustive-deps

    // "Expand Players" stage: open players + FO sub-sections + retail/virtual
    // containers, but keep every bundle collapsed so you see the player tree
    // without the items underneath.
    useEffect(() => {
        if (!expandPlayersNonce) return;
        setOpenPlayers(new Set(playerGroups.map(p => p.key)));
        setOpenFos(new Set(Array.isArray(subSections) ? subSections.map(s => s.key) : []));
        setOpenRetail(true);
        setOpenVirtual(true);
        setOpenBundles(new Set());
    }, [expandPlayersNonce]); // eslint-disable-line react-hooks/exhaustive-deps

    const notify = () => { if (typeof onUserToggle === 'function') onUserToggle(); };
    const togglePlayer = (k) => { notify(); setOpenPlayers(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; }); };
    const toggleBundle = (k) => { notify(); setOpenBundles(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; }); };
    const toggleFo     = (k) => { notify(); setOpenFos(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; }); };

    const COL_COUNT = 9;

    return (
        <table className={`odv2-table ${density === 'compact' ? 'odv2--compact' : density === 'comfortable' ? 'odv2--comfortable' : density === 'maximum' ? 'odv2--comfortable odv2--maximum' : ''} ${modifyBar ? 'odv2-table--modify' : ''}`}>
            <thead>
                <tr className="odv2-th-row">
                    <th style={{ width: 28 }} />
                    <th>Item</th>
                    <th style={{ width: 160 }}>SKU</th>
                    <th style={{ width: 70 }}>Variant</th>
                    <th className="odv2-th-center" style={{ width: 50 }}>Qty</th>
                    <th className="odv2-th-stage"><div className="odv2-th-stage-rot">Picked</div></th>
                    <th className="odv2-th-stage"><div className="odv2-th-stage-rot">Pressed</div></th>
                    <th className="odv2-th-stage"><div className="odv2-th-stage-rot">Packed</div></th>
                    <th className="odv2-th-right" style={{ width: 90 }}>Price</th>
                </tr>
                <ModifyBarRow controls={modifyBar} />
            </thead>
            <tbody>
                {playerGroups.map((pg, gi) => {
                    const playerOpen = openPlayers.has(pg.key);
                    const bundleDots = pg.bundles.map(b => dotsForBundle(b.parent, b.children, portalState, fulfillmentShipped, lineFlags));
                    const playerDots = rollupStates(bundleDots);
                    const playerPrice = pg.bundles.reduce((sum, b) => {
                        const p = priceOf(b.parent.id) || 0;
                        const childSum = b.children.reduce((s, c) => s + (priceOf(c.id) || 0), 0);
                        return sum + p + childSum;
                    }, 0);

                    const subGroups = groupBundlesBySubSection(pg.bundles, subSections || []);
                    const isSingleSub = subGroups.length === 1;
                    const inlineSub = isSingleSub && subGroups[0].subSection.fo != null
                        ? subGroups[0].subSection : null;

                    return (
                        <React.Fragment key={pg.key}>
                            <tr className={`odv2-row-player ${gi === 0 ? 'first' : ''}`}
                                data-player-key={pg.key}>
                                <td>
                                    <span className={`odv2-caret ${playerOpen ? 'odv2-caret--open' : ''}`}
                                          onClick={() => togglePlayer(pg.key)}>▶</span>
                                </td>
                                <td>
                                    <div className="odv2-player-cell">
                                        <Avatar name={pg.name} />
                                        <div>
                                            <div className="odv2-player-name">
                                                <PlayerNumber number={pg.number} override={pg.numberOverride} cls="odv2-jersey" />
                                                {pg.name}
                                                {pg.club && <span className="odv2-club">{pg.club}</span>}
                                                {inlineSub && <FoStatusInline subSection={inlineSub} order={order} />}
                                            </div>
                                            <div className="odv2-player-meta">
                                                <PlayerTeam team={pg.team} override={pg.teamOverride} />
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td colSpan={3}>
                                    <span className="odv2-bundle-count">{pg.bundles.length} bundle{pg.bundles.length !== 1 ? 's' : ''}</span>
                                </td>
                                <StageDotCells dots={playerDots} />
                                <td className="odv2-cell-right"><span className="odv2-price">${playerPrice.toFixed(2)}</span></td>
                            </tr>

                            {playerOpen && subGroups.map(({ subSection, bundles: subBundles }) => {
                                const showFoHeader = !isSingleSub;
                                const foOpen = !showFoHeader || openFos.has(subSection.key);
                                return (
                                    <React.Fragment key={`${pg.key}::${subSection.key}`}>
                                        {showFoHeader && (
                                            <FoSubHeaderRow
                                                subSection={subSection}
                                                label={null}
                                                open={foOpen}
                                                onToggle={() => toggleFo(subSection.key)}
                                                colSpan={COL_COUNT}
                                                order={order}
                                                dots={foRollupDots(subBundles, portalState, fulfillmentShipped, lineFlags)}
                                            />
                                        )}
                                        {foOpen && subBundles.map((b, bi) => {
                                            const bundleKey = `${pg.key}::${subSection.key}::${b.parent.id}::${bi}`;
                                            return renderBundleAndDecos({ splitBundleMap, npCtx, lineFlags, tpReq, tpOv,
                                                b, bundleKey,
                                                openBundles, toggleBundle,
                                                portalState, fulfillmentShipped, priceOf,
                                                bundleDotsForB: dotsForBundle(b.parent, b.children, portalState, fulfillmentShipped, lineFlags),
                                                modifyMode, pendingChanges, onTogglePending,
                                                foLocId: foLocIdOf(subSection),
                                            });
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </React.Fragment>
                    );
                })}

                {retailBundles.length > 0 && (() => {
                    const retailSubGroups = groupBundlesBySubSection(retailBundles, subSections || []);
                    const isSingleSub = retailSubGroups.length === 1;
                    const inlineSub = isSingleSub && retailSubGroups[0].subSection.fo != null
                        ? retailSubGroups[0].subSection : null;
                    const retailDots = rollupStates(retailBundles.map(b => dotsForBundle(b.parent, b.children, portalState, fulfillmentShipped, lineFlags)));
                    const retailPrice = retailBundles.reduce((sum, b) => {
                        const p = priceOf(b.parent.id) || 0;
                        const childSum = b.children.reduce((s, c) => s + (priceOf(c.id) || 0), 0);
                        return sum + p + childSum;
                    }, 0);
                    return (
                        <React.Fragment key="__retail__">
                            <tr className="odv2-row-player odv2-row-retail"
                                data-player-key="__retail__">
                                <td>
                                    <span className={`odv2-caret ${openRetail ? 'odv2-caret--open' : ''}`}
                                          onClick={() => { notify(); setOpenRetail(s => !s); }}>▶</span>
                                </td>
                                <td>
                                    <div className="odv2-player-cell">
                                        <span className="odv2-avatar odv2-avatar--retail" aria-hidden>R</span>
                                        <div>
                                            <div className="odv2-player-name">
                                                Retail Items
                                                {inlineSub && <FoStatusInline subSection={inlineSub} order={order} />}
                                            </div>
                                            <div className="odv2-player-meta">
                                                <span>Items not assigned to a player</span>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td colSpan={3}>
                                    <span className="odv2-bundle-count">{retailBundles.length} item{retailBundles.length !== 1 ? 's' : ''}</span>
                                </td>
                                <StageDotCells dots={retailDots} />
                                <td className="odv2-cell-right"><span className="odv2-price">${retailPrice.toFixed(2)}</span></td>
                            </tr>

                            {openRetail && retailSubGroups.map(({ subSection, bundles: subBundles }) => {
                                const showFoHeader = !isSingleSub;
                                const foOpen = !showFoHeader || openFos.has(subSection.key);
                                return (
                                    <React.Fragment key={`__retail__::${subSection.key}`}>
                                        {showFoHeader && (
                                            <FoSubHeaderRow
                                                subSection={subSection}
                                                label={null}
                                                open={foOpen}
                                                onToggle={() => toggleFo(subSection.key)}
                                                colSpan={COL_COUNT}
                                                order={order}
                                                dots={foRollupDots(subBundles, portalState, fulfillmentShipped, lineFlags)}
                                            />
                                        )}
                                        {foOpen && subBundles.map((b, bi) => {
                                            const bundleKey = `__retail__::${subSection.key}::${b.parent.id}::${bi}`;
                                            return renderBundleAndDecos({ splitBundleMap, npCtx, lineFlags, tpReq, tpOv,
                                                b, bundleKey,
                                                openBundles, toggleBundle,
                                                portalState, fulfillmentShipped, priceOf,
                                                bundleDotsForB: dotsForBundle(b.parent, b.children, portalState, fulfillmentShipped, lineFlags),
                                                modifyMode, pendingChanges, onTogglePending,
                                                foLocId: foLocIdOf(subSection),
                                            });
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </React.Fragment>
                    );
                })()}

                {virtualLineItems.length > 0 && (() => {
                    let fulfilledCount = 0;
                    for (const li of virtualLineItems) {
                        if (shippedLineIds?.has(li.id)) fulfilledCount++;
                    }
                    const total = virtualLineItems.length;
                    const rollup = fulfilledCount === total ? 'fulfilled'
                                 : fulfilledCount === 0     ? 'unfulfilled'
                                 : 'partial';
                    const rollupLabel = rollup === 'fulfilled' ? 'Fulfilled'
                                      : rollup === 'partial'   ? 'Partial'
                                      : 'Unfulfilled';
                    const rollupCls = rollup === 'fulfilled' ? 'odv2-virt-chip--ok'
                                    : rollup === 'partial'   ? 'odv2-virt-chip--partial'
                                    : 'odv2-virt-chip--pending';
                    const virtualPrice = virtualLineItems.reduce((s, li) => s + (priceOf(li.id) || 0), 0);
                    return (
                        <React.Fragment key="__virtual__">
                            <tr className="odv2-row-player odv2-row-virtual"
                                data-player-key="__virtual__">
                                <td>
                                    <span className={`odv2-caret ${openVirtual ? 'odv2-caret--open' : ''}`}
                                          onClick={() => { notify(); setOpenVirtual(s => !s); }}>▶</span>
                                </td>
                                <td>
                                    <div className="odv2-player-cell">
                                        <span className="odv2-avatar odv2-avatar--virtual" aria-hidden>V</span>
                                        <div>
                                            <div className="odv2-player-name">
                                                Virtual Items
                                                <span className={`odv2-virt-chip ${rollupCls}`}>{rollupLabel}</span>
                                            </div>
                                            <div className="odv2-player-meta">
                                                <span>Processing fee, returns, package protection</span>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td colSpan={3}>
                                    <span className="odv2-bundle-count">{total} item{total !== 1 ? 's' : ''}</span>
                                </td>
                                <td colSpan={3} />
                                <td className="odv2-cell-right"><span className="odv2-price">${virtualPrice.toFixed(2)}</span></td>
                            </tr>
                            {openVirtual && virtualLineItems.map(li => {
                                const fulfilled = shippedLineIds?.has(li.id) ?? null;
                                return <VirtualRow key={li.id} li={li} priceOf={priceOf} fulfilled={fulfilled} />;
                            })}
                        </React.Fragment>
                    );
                })()}
            </tbody>
        </table>
    );
}

// ── Fulfillments view: same row rendering as ItemsTree, grouped by FO ───────
function FulfillmentsTree({ sortMode = 'order', bundles, portalState, fulfillmentShipped, shippedLineIds, priceOf, density, subSections, expandAllNonce, compressAllNonce, onUserToggle, modifyMode, pendingChanges, onTogglePending, modifyBar, order = null }) {
    const lineFlags = useContext(LineFlagsContext);
    const tpReq = useContext(TpReqContext);
    const tpOv = useContext(TpOverridesContext);
    const splitBundleMap = useContext(JumbleContext);
    const npCtx = useContext(NpContext);
    const ffRoster = useMemo(() => parseRoster(order), [order]);
    const [openFos, setOpenFos] = useState(
        () => new Set(Array.isArray(subSections) ? subSections.map(s => s.key) : [])
    );
    const [openBundles, setOpenBundles] = useState(() => new Set());
    // Refunded sub-section starts OPEN by default — the operator is most
    // likely landing here precisely to see what was refunded. Caret in the
    // header row toggles it like any other FO section.
    const [refundedClosed, setRefundedClosed] = useState(false);

    useEffect(() => {
        if (!Array.isArray(subSections)) return;
        setOpenFos(new Set(subSections.map(s => s.key)));
    }, [subSections]);

    useEffect(() => {
        if (!expandAllNonce) return;
        setOpenFos(new Set(Array.isArray(subSections) ? subSections.map(s => s.key) : []));
        const allKeys = new Set();
        const grouped = groupBundlesBySubSection(bundles, subSections || []);
        grouped.forEach(({ subSection, bundles: foBundles }) => {
            foBundles.forEach((b, bi) => {
                allKeys.add(`fo::${subSection.key}::${b.parent.id}::${bi}`);
            });
        });
        setOpenBundles(allKeys);
    }, [expandAllNonce]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!compressAllNonce) return;
        setOpenFos(new Set());
        setOpenBundles(new Set());
    }, [compressAllNonce]); // eslint-disable-line react-hooks/exhaustive-deps

    const notify = () => { if (typeof onUserToggle === 'function') onUserToggle(); };
    const toggleFo     = (k) => { notify(); setOpenFos(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; }); };
    const toggleBundle = (k) => { notify(); setOpenBundles(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; }); };

    const COL_COUNT = 9;
    // Drop fully-refunded bundles from the main rendering so they don't show
    // up twice. A bundle is "fully refunded" when its parent's currentQuantity
    // is 0 AND every child has currentQuantity 0 — i.e. nothing remains on the
    // order. Partial refunds (parent.qty > 0) keep showing in their original
    // FO section with the remaining count AND in the Refunded section below
    // with the removed count, matching V1's RemovedItemsCard behavior.
    const visibleBundles = (bundles || []).filter(b => {
        const parentQty = b.parent?.quantity ?? 0;
        const anyChildQty = (b.children || []).some(c => (c.quantity ?? 0) > 0);
        return parentQty > 0 || anyChildQty;
    });

    // Group ALL bundles by sub-section — including virtuals. Virtual bundles
    // get rendered with VirtualRow inside their FO sub-section so the
    // processing fee (and Free Returns / Package Protection) show under the
    // same FO header as a normal item. NONE-method FOs are real sub-sections
    // now, so their headers will show location + date but no tracking.
    const grouped = groupBundlesBySubSection(visibleBundles, subSections || []);

    // Refunded / removed items section. Backend stamps each LI with
    // `originalQuantity` = qty at order-create time and `quantity` =
    // currentQuantity (post-edit / post-refund). Anything where original >
    // quantity has been taken off the order — refund + restock, or Shopify
    // Order Editing. Render those line items as their own FO-styled section
    // at the end of the table so the operator can see what's gone without
    // diffing against Shopify Admin.
    const refundedRows = [];
    for (const b of (bundles || [])) {
        const sweep = [b.parent, ...(b.children || [])];
        for (const li of sweep) {
            if (!li || isVirtual(li.title)) continue;
            const orig = li.originalQuantity ?? li.quantity ?? 0;
            const cur  = li.quantity || 0;
            if (orig <= cur) continue;
            const removedQty = orig - cur;
            refundedRows.push({
                gid: `__refunded__${li.id}`,
                parent: { ...li, quantity: removedQty, currentQuantity: removedQty },
                children: [],
            });
        }
    }
    const refundedSubSection = refundedRows.length === 0 ? null : {
        key: '__refunded__',
        displayMode: 'refunded',
        fo: null,
        fulfillment: null,
        lineItemIds: new Set(refundedRows.map(b => b.parent.id)),
    };
    const refundedOpen = !refundedClosed;

    return (
        <table className={`odv2-table odv2-table--fulfillments ${density === 'compact' ? 'odv2--compact' : density === 'comfortable' ? 'odv2--comfortable' : density === 'maximum' ? 'odv2--comfortable odv2--maximum' : ''} ${modifyBar ? 'odv2-table--modify' : ''}`}>
            <thead>
                <tr className="odv2-th-row">
                    <th style={{ width: 28 }} />
                    <th>Item</th>
                    <th style={{ width: 160 }}>SKU</th>
                    <th style={{ width: 70 }}>Variant</th>
                    <th className="odv2-th-center" style={{ width: 50 }}>Qty</th>
                    <th className="odv2-th-stage"><div className="odv2-th-stage-rot">Picked</div></th>
                    <th className="odv2-th-stage"><div className="odv2-th-stage-rot">Pressed</div></th>
                    <th className="odv2-th-stage"><div className="odv2-th-stage-rot">Packed</div></th>
                    <th className="odv2-th-right" style={{ width: 90 }}>Price</th>
                </tr>
                <ModifyBarRow controls={modifyBar} />
            </thead>
            <tbody>
                {grouped.map(({ subSection, bundles: foBundles }) => {
                    const foOpen = openFos.has(subSection.key);
                    return (
                        <React.Fragment key={subSection.key}>
                            <FoSubHeaderRow
                                subSection={subSection}
                                label={null}
                                open={foOpen}
                                onToggle={() => toggleFo(subSection.key)}
                                colSpan={COL_COUNT}
                                order={order}
                                dots={foRollupDots(foBundles, portalState, fulfillmentShipped, lineFlags)}
                            />
                            {foOpen && sortFoBundles(foBundles, sortMode, ffRoster).map((b, bi) => {
                                const bundleKey = `fo::${subSection.key}::${b.parent.id}::${bi}`;
                                if (isVirtual(b.parent?.title)) {
                                    const fulfilled = shippedLineIds?.has(b.parent.id) ?? null;
                                    return <VirtualRow key={bundleKey} li={b.parent} priceOf={priceOf} fulfilled={fulfilled} />;
                                }
                                const bundleDotsForB = dotsForBundle(b.parent, b.children, portalState, fulfillmentShipped, lineFlags);
                                return (
                                    <React.Fragment key={bundleKey}>
                                        {renderBundleAndDecos({ splitBundleMap, npCtx, lineFlags, tpReq, tpOv,
                                            b, bundleKey, openBundles, toggleBundle,
                                            portalState, fulfillmentShipped, priceOf, bundleDotsForB,
                                            modifyMode, pendingChanges, onTogglePending,
                                            foLocId: foLocIdOf(subSection),
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </React.Fragment>
                    );
                })}

                {/* Refunded / removed items — own FO-styled sub-section rendered
                  * at the bottom. Synthetic subSection so the existing
                  * FoSubHeaderRow + bundle row renderer can be reused as-is. */}
                {refundedSubSection && (
                    <React.Fragment key={refundedSubSection.key}>
                        <FoSubHeaderRow
                            subSection={refundedSubSection}
                            label={null}
                            open={refundedOpen}
                            onToggle={() => { notify(); setRefundedClosed(v => !v); }}
                            colSpan={COL_COUNT}
                            order={order}
                        />
                        {refundedOpen && refundedRows.map((b, bi) => {
                            const bundleKey = `fo::__refunded__::${b.parent.id}::${bi}`;
                            const bundleDotsForB = dotsForBundle(b.parent, b.children, portalState, fulfillmentShipped, lineFlags);
                            return (
                                <React.Fragment key={bundleKey}>
                                    {renderBundleAndDecos({ splitBundleMap, npCtx, lineFlags, tpReq, tpOv,
                                        b, bundleKey, openBundles, toggleBundle,
                                        portalState, fulfillmentShipped, priceOf, bundleDotsForB,
                                        modifyMode, pendingChanges, onTogglePending,
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </React.Fragment>
                )}
            </tbody>
        </table>
    );
}

// ── Fulfillments strip — one card per FO sub-section ────────────────────────
function FulfillmentsStrip({ subSections, bundles, activeKey, onPick }) {
    if (!Array.isArray(subSections) || subSections.length === 0) return null;
    const handlePick = (key) => {
        if (typeof onPick === 'function') onPick(key);
        const row = document.querySelector(`tr.odv2-row-fo[data-fo-key="${CSS.escape(key)}"]`);
        if (!row) return;
        const scroller = document.querySelector('.page-content') || document.scrollingElement || document.documentElement;
        const cs = getComputedStyle(document.documentElement);
        const heroH = parseInt(cs.getPropertyValue('--odv2-hero-h'), 10) || 260;
        const rosterH = parseInt(cs.getPropertyValue('--odv2-roster-h'), 10)
            || (document.querySelector('.odv2-roster')?.offsetHeight ?? 0);
        const toolbarH = parseInt(cs.getPropertyValue('--odv2-toolbar-h'), 10)
            || (document.querySelector('.odv2-panel-toolbar')?.offsetHeight ?? 49);
        const theadH = parseInt(cs.getPropertyValue('--odv2-thead-h'), 10)
            || (document.querySelector('.odv2-table thead')?.offsetHeight ?? 85);
        const top = row.getBoundingClientRect().top + scroller.scrollTop
            - scroller.getBoundingClientRect().top - heroH - rosterH - toolbarH - theadH;
        scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    };
    // qty sum across every line item (parent + child) whose id is in this FO's
    // lineItemIds. Row counts under-report: a 1-line-item FO can carry qty 29.
    const qtyOf = (li) => parseInt(li?.currentQuantity ?? li?.quantity ?? 0, 10) || 0;
    return (
        <div className="odv2-roster">
            {subSections.map(s => {
                const status = formatFoStatus(s);
                const loc = getFoLocationName(s);
                const fulfilledAt = s?.fulfillment?.createdAt;
                const dateStr = fulfilledAt
                    ? new Date(fulfilledAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                    : '';
                const itemCount = (bundles || []).reduce((acc, b) => {
                    let n = 0;
                    if (s.lineItemIds?.has(b.parent?.id)) n += qtyOf(b.parent);
                    for (const c of (b.children || [])) {
                        if (s.lineItemIds?.has(c.id)) n += qtyOf(c);
                    }
                    return acc + n;
                }, 0);
                const isFulfilled = status === 'Fulfilled' || isSubSectionDone(s) || !!fulfilledAt;
                return (
                    <div key={s.key}
                         className={`odv2-roster-card odv2-roster-card--fo ${activeKey === s.key ? 'odv2-roster-card--active' : ''}`}
                         onClick={() => handlePick(s.key)}>
                        <span className={`odv2-roster-num odv2-roster-num--fo ${isFulfilled ? 'odv2-roster-num--ok' : ''}`}>
                            {isFulfilled ? '✓' : '!'}
                        </span>
                        <div>
                            <div className="odv2-roster-name">{loc || (status ? status.replace(/_/g, ' ') : 'Fulfillment')}</div>
                            <div className="odv2-roster-meta">
                                <span className="odv2-roster-bundles">{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
                                {dateStr && <span className="odv2-roster-bundles">· {dateStr}</span>}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ── Roster strip ────────────────────────────────────────────────────────────
// Turn the raw TP specialization enum into a display label (V1 SPEC_LABELS).
const SPEC_LABELS = {
    regular: 'Regular', captain: 'Captain', goalkeeper: 'Goalkeeper',
    returning_player: 'Returning Player', returning_goalkeeper: 'Returning Goalkeeper',
};
const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : '');

function RosterStrip({ playerGroups, retailBundles, activeKey, onPick, tpPlayers = [] }) {
    // Live TP roster data (spec / role) keyed by user_id — prefer it over the
    // designation snapshotted at checkout, same as V1's PlayersPanel.
    const tpByPid = useMemo(() => {
        const m = {};
        for (const p of (tpPlayers || [])) if (p && p.user_id != null) m[String(p.user_id)] = p;
        return m;
    }, [tpPlayers]);
    const hasRetail = (retailBundles?.length ?? 0) > 0;
    const totalCards = playerGroups.length + (hasRetail ? 1 : 0);
    if (totalCards === 0) return null;
    const handlePick = (key) => {
        onPick(key);
        const row = document.querySelector(`tr.odv2-row-player[data-player-key="${CSS.escape(key)}"]`);
        if (!row) return;
        const scroller = document.querySelector('.page-content') || document.scrollingElement || document.documentElement;
        const cs = getComputedStyle(document.documentElement);
        const heroH = parseInt(cs.getPropertyValue('--odv2-hero-h'), 10) || 260;
        const rosterH = parseInt(cs.getPropertyValue('--odv2-roster-h'), 10)
            || (document.querySelector('.odv2-roster')?.offsetHeight ?? 0);
        const toolbarH = parseInt(cs.getPropertyValue('--odv2-toolbar-h'), 10)
            || (document.querySelector('.odv2-panel-toolbar')?.offsetHeight ?? 49);
        const theadH = parseInt(cs.getPropertyValue('--odv2-thead-h'), 10)
            || (document.querySelector('.odv2-table thead')?.offsetHeight ?? 85);
        const top = row.getBoundingClientRect().top + scroller.scrollTop
            - scroller.getBoundingClientRect().top - heroH - rosterH - toolbarH - theadH;
        scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    };
    // "items" = sum of qty across the group's bundle parents + children.
    // "bundles" = count of pressable bundles (with embellishment children).
    const qtyOf = (li) => parseInt(li?.currentQuantity ?? li?.quantity ?? 0, 10) || 0;
    const sumItems = (bs) => bs.reduce((s, b) =>
        s + qtyOf(b.parent) + (b.children || []).reduce((cs, c) => cs + qtyOf(c), 0), 0);
    const countPressable = (bs) => bs.filter(b => Array.isArray(b.children) && b.children.length > 0).length;
    return (
        <div className="odv2-roster">
            {playerGroups.map(pg => {
                const itemsQty = sumItems(pg.bundles);
                const bundlesQty = countPressable(pg.bundles);
                return (
                <div key={pg.key}
                     className={`odv2-roster-card ${activeKey === pg.key ? 'odv2-roster-card--active' : ''}`}
                     onClick={() => handlePick(pg.key)}>
                    {pg.numberOverride
                        ? <span className="odv2-roster-numwrap odv2-roster-numwrap--stack">
                              <span className="odv2-roster-num odv2-roster-num--orig" title="Original number from order">#{pg.number || pg.numberOverride.order_number || '—'}</span>
                              <span className="odv2-roster-num odv2-roster-num--ov" title={`Team Portal number override${pg.numberOverride.tp_updated_at ? ` (updated ${pg.numberOverride.tp_updated_at})` : ''}`}>#{pg.numberOverride.override_number}</span>
                          </span>
                        : pg.number && <span className="odv2-roster-num">#{pg.number}</span>}
                    <div>
                        <div className="odv2-roster-name">
                            {pg.name}
                            <span className="odv2-roster-counts">{itemsQty} item{itemsQty !== 1 ? 's' : ''} · {bundlesQty} bundle{bundlesQty !== 1 ? 's' : ''}</span>
                        </div>
                        {(pg.team || pg.teamOverride) && (
                            <div className="odv2-roster-meta">
                                <span className="odv2-roster-bundles"><PlayerTeam team={pg.team} override={pg.teamOverride} /></span>
                            </div>
                        )}
                        {(() => {
                            const tpp = pg.playerId != null ? tpByPid[String(pg.playerId)] : null;
                            const spec = tpp?.specialization;
                            const role = tpp?.role || pg.role;
                            const specLabel = spec ? (SPEC_LABELS[spec] || cap(spec.replace(/_/g, ' '))) : null;
                            const badges = [
                                pg.gender && ['gender', cap(pg.gender)],
                                specLabel && ['spec', specLabel],
                                role && ['role', cap(role)],
                            ].filter(Boolean);
                            return badges.length ? (
                                <div className="odv2-roster-badges">
                                    {badges.map(([k, b]) => <span key={k} className={`odv2-roster-badge odv2-roster-badge--${k}`}>{b}</span>)}
                                </div>
                            ) : null;
                        })()}
                    </div>
                    {/* V1's "⚠ Team Portal … Override" flags, to the right of name + team (Eric 2026-09-16) */}
                    {(pg.numberOverride || pg.teamOverride) && (
                        <div className="odv2-roster-warns">
                            {pg.numberOverride && <div className="odv2-roster-warn">⚠ Team Portal Number Override</div>}
                            {pg.teamOverride && <div className="odv2-roster-warn">⚠ Team Portal Team Override</div>}
                        </div>
                    )}
                </div>
                );
            })}
            {hasRetail && (() => {
                const itemsQty = sumItems(retailBundles);
                const bundlesQty = countPressable(retailBundles);
                return (
                <div key="__retail__"
                     className={`odv2-roster-card odv2-roster-card--retail ${activeKey === '__retail__' ? 'odv2-roster-card--active' : ''}`}
                     onClick={() => handlePick('__retail__')}>
                    <span className="odv2-roster-num odv2-roster-num--retail">R</span>
                    <div>
                        <div className="odv2-roster-name">
                            Retail Items
                            <span className="odv2-roster-counts">{itemsQty} item{itemsQty !== 1 ? 's' : ''} · {bundlesQty} bundle{bundlesQty !== 1 ? 's' : ''}</span>
                        </div>
                    </div>
                </div>
                );
            })()}
        </div>
    );
}

// ── main component ──────────────────────────────────────────────────────────
export default function OrderDetailV2() {
    const { orderId, storeSlug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, emulating, startEmulation, stopEmulation } = useAuth();
    const { activeStore, STORE_DISPLAY_NAMES: _storeNames } = useStore();
    const _storeFetch = useAuthFetch();
    // v3 layout hooks — null when running as plain v2
    const stagesStripFn = useContext(StagesStripContext);
    const sidebarHeadFn = useContext(SidebarHeadContext);
    // Latest fetch helper without making effects depend on it (a sidebar store
    // switch changes its identity; the order page must not react to that).
    const _storeFetchRef = useRef(_storeFetch);
    _storeFetchRef.current = _storeFetch;

    const [order, setOrder] = useState(null);
    const [portalState, setPortalState] = useState({});
    // ── V1 parity (2026-09-15, Eric) ──────────────────────────────────────
    const [viewers, setViewers]                   = useState([]);   // presence circles
    const [skuNotifications, setSkuNotifications] = useState({});   // {sku: message}
    const [numberPreviews, setNumberPreviews]     = useState({});   // number-preview metaobjects
    const [storedLabels, setStoredLabels]         = useState([]);   // shipping labels bought through OpsHub
    const [labelsNonce, setLabelsNonce]           = useState(0);
    useEffect(() => {   // presence heartbeat every 8 s while viewing this order (V1 rule)
        if (!orderId || orderId === 'null') return;
        const beat = () => {
            _storeFetch(`/api/orders/${orderId}/presence`, { method: 'POST' })
                .then(r => r.ok ? r.json() : [])
                .then(v => setViewers(Array.isArray(v) ? v : []))
                .catch(() => {});
        };
        beat();
        const t = setInterval(beat, 8000);
        return () => clearInterval(t);
    }, [orderId]); // eslint-disable-line
    useEffect(() => {
        if (!orderId || orderId === 'null') return;
        const f = _storeFetchRef.current;
        f(`/api/sku-notifications/for-order/${orderId}`)
            .then(r => r.ok ? r.json() : { sku_notifications: {} })
            .then(j => setSkuNotifications(j.sku_notifications || {}))
            .catch(() => setSkuNotifications({}));
        f(`/api/orders/${orderId}/number-previews`)
            .then(r => r.ok ? r.json() : {})
            .then(j => setNumberPreviews(j?.previews || j || {}))
            .catch(() => setNumberPreviews({}));
    }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps
    // V1 parity (2026-09-16): a fulfilled order with no stored label asks the
    // backend to recover labels bought outside OpsHub (same self-healing path
    // as the Packing Portal), then re-reads. Only the roles the recover
    // endpoint accepts, so CS page views don't fire guaranteed 403s.
    const _labelsFs = order?.displayFulfillmentStatus || '';
    useEffect(() => {
        if (!orderId || orderId === 'null' || !order) return;
        let cancelled = false;
        (async () => {
            const f = _storeFetchRef.current;
            const fetchLabels = async () => {
                const r = await f(`/api/orders/${orderId}/shipping-labels`);
                if (!r.ok) return [];
                const d = await r.json();
                return d.labels || [];
            };
            try {
                let arr = await fetchLabels();
                const st = _labelsFs.toUpperCase();
                const isShipped = st === 'FULFILLED' || st === 'PARTIALLY_FULFILLED';
                const canRecover = hasRole(user, ['admin', 'franchise', 'site_manager', 'user', 'store_manager']);
                if (arr.length === 0 && isShipped && canRecover) {
                    try {
                        await f(`/api/orders/${orderId}/shipping-labels/recover`, { method: 'POST' });
                        arr = await fetchLabels();
                    } catch (_) { /* keep empty */ }
                }
                if (!cancelled) setStoredLabels(arr);
            } catch (_) { if (!cancelled) setStoredLabels([]); }
        })();
        return () => { cancelled = true; };
    }, [orderId, labelsNonce, _labelsFs, !!order]); // eslint-disable-line react-hooks/exhaustive-deps
    // Custom printout footer (Settings → Printout Footer) — V1 parity.
    const [printoutFooterHtml, setPrintoutFooterHtml] = useState(null);
    useEffect(() => {
        const sk = activeStore || storeSlug || 'soccerpost';
        let cancelled = false;
        _storeFetchRef.current(`/api/store/printout-footer?store=${encodeURIComponent(sk)}`)
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (!cancelled && d?.html != null) setPrintoutFooterHtml(d.html); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps
    // ?loc=<shopify_location_id> ⇄ sidebar emulation, same rules as V1: an
    // incoming ?loc= starts emulation for that location once the location list
    // is known; changing emulation rewrites ?loc= in place (replace, no reload).
    const [urlEmuLocs, setUrlEmuLocs] = useState([]);
    useEffect(() => {
        let cancelled = false;
        _storeFetch('/api/locations').then(r => r.ok ? r.json() : []).then(rows => {
            if (!cancelled) setUrlEmuLocs(Array.isArray(rows) ? rows : []);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [_storeFetch, activeStore]);
    const urlLocParam = searchParams.get('loc');
    const lastSyncedLoc = useRef(null);
    useEffect(() => {
        if (urlLocParam == null) return;
        if (String(lastSyncedLoc.current) === String(urlLocParam)) return;
        if (urlLocParam === 'all' || urlLocParam === '') {
            lastSyncedLoc.current = 'all';
            if (emulating) stopEmulation();
            return;
        }
        if (!urlEmuLocs.length) return;
        const target = urlEmuLocs.find(l => String(l.shopify_location_id) === String(urlLocParam));
        if (!target) return;
        lastSyncedLoc.current = String(target.shopify_location_id);
        if (emulating?.shopify_location_id !== target.shopify_location_id) {
            startEmulation({
                id: target.id, name: target.name,
                shopify_location_id: target.shopify_location_id,
                is_franchise: target.is_franchise || false,
                store_key: activeStore,
            });
        }
    }, [urlLocParam, urlEmuLocs, emulating, startEmulation, stopEmulation, activeStore]);
    useEffect(() => {
        if (!urlEmuLocs.length) return;
        const desired = emulating?.shopify_location_id ? String(emulating.shopify_location_id) : 'all';
        if (String(lastSyncedLoc.current) === desired) return;
        if (String(urlLocParam || '') === desired) { lastSyncedLoc.current = desired; return; }
        lastSyncedLoc.current = desired;
        const next = new URLSearchParams(searchParams);
        next.set('loc', desired);
        setSearchParams(next, { replace: true });
    }, [emulating, urlLocParam, urlEmuLocs, searchParams, setSearchParams]);
    // Shopify-style "Review address issues" dialog (same endpoints as V1).
    const [addrReviewOpen, setAddrReviewOpen] = useState(false);
    const [addrReview, setAddrReview]         = useState(null);
    const [addrMode, setAddrMode]             = useState('review');
    const [addrChoice, setAddrChoice]         = useState('suggestion');
    const [addrSaveCust, setAddrSaveCust]     = useState(false);
    const [addrForm, setAddrForm]             = useState(null);
    const [addrBusy, setAddrBusy]             = useState(false);
    const [addrErr, setAddrErr]               = useState('');
    const openAddrReview = async () => {
        setAddrReviewOpen(true); setAddrReview(null); setAddrErr(''); setAddrMode('review');
        try {
            const r = await _storeFetch(`/api/orders/${orderId}/address-review`);
            const d = await r.json();
            if (!r.ok) { setAddrErr(d.detail || 'Could not load address review.'); return; }
            setAddrReview(d); setAddrChoice(d.suggestion ? 'suggestion' : 'current'); setAddrForm({ ...(d.current || {}) });
        } catch (e) { setAddrErr('Could not load address review.'); }
    };
    const applyAddress = async (address, source) => {
        setAddrBusy(true); setAddrErr('');
        try {
            const r = await _storeFetch(`/api/orders/${orderId}/shipping-address`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address, save_to_customer: addrSaveCust, source }),
            });
            const d = await r.json();
            if (!r.ok) { setAddrErr(d.detail || 'Update failed.'); return; }
            setAddrReviewOpen(false);
            window.location.reload();
        } catch (e) { setAddrErr('Update failed.'); }
        finally { setAddrBusy(false); }
    };
    const addrLine = (a) => a ? [a.address1, a.address2].filter(Boolean).join(', ') : '';
    // Prev / Next — the ordered id list the Orders list stashes in sessionStorage
    // (same rules as V1: same store only; deep links get no footer).
    const prevNextCursor = useMemo(() => {
        const sk = activeStore || storeSlug || 'soccerpost';
        try {
            const raw = sessionStorage.getItem('ordersList');
            if (!raw) return { ids: [], idx: -1, store: sk };
            const env = JSON.parse(raw);
            const ids = Array.isArray(env?.ids) ? env.ids : [];
            if ((env?.store || sk) !== sk) return { ids: [], idx: -1, store: sk };
            return { ids, idx: ids.indexOf(String(orderId)), store: sk };
        } catch { return { ids: [], idx: -1, store: sk }; }
    }, [orderId, activeStore, storeSlug]);
    const prevOrderId = prevNextCursor.idx > 0 ? prevNextCursor.ids[prevNextCursor.idx - 1] : null;
    const nextOrderId = (prevNextCursor.idx >= 0 && prevNextCursor.idx < prevNextCursor.ids.length - 1)
        ? prevNextCursor.ids[prevNextCursor.idx + 1] : null;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 2026-09-16 (Eric): the old 'timeline' tab became two — 'visual' (Visual Timeline) and 'eventlog' (Order Event Log).
    const VALID_TABS = useMemo(() => ['players', 'fulfillments', 'visual', 'eventlog'], []);
    const TL_TABS = useMemo(() => new Set(['visual', 'eventlog']), []);
    const tabFromUrl = (() => {
        const t = searchParams.get('tab');
        if (t === 'timeline') return 'visual';
        return VALID_TABS.includes(t) ? t : 'players';
    })();
    const [activeTab, setActiveTab] = useState(tabFromUrl);
    // Tabs visited so far — the Order Event Log stays mounted (hidden) after its
    // first visit so 'View Full Event Log' and the scroll position survive a
    // trip to Players / Fulfillments (Eric 2026-09-16).
    const [visitedTabs, setVisitedTabs] = useState(() => new Set([tabFromUrl]));
    useEffect(() => { setVisitedTabs(prev => prev.has(activeTab) ? prev : new Set([...prev, activeTab])); }, [activeTab]);
    // Sync activeTab → URL (so each tab is linkable). Preserve other params (?v=2 etc).
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        const current = next.get('tab');
        if (activeTab === 'players') {
            if (current != null) next.delete('tab');
            else return;
        } else if (current === activeTab) {
            return;
        } else {
            next.set('tab', activeTab);
        }
        navigate({ pathname: location.pathname, search: next.toString() ? `?${next.toString()}` : '' }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);
    // Sync URL → activeTab on back/forward navigation.
    useEffect(() => {
        const t = searchParams.get('tab');
        const next = t === 'timeline' ? 'visual' : (VALID_TABS.includes(t) ? t : 'players');
        if (next !== activeTab) setActiveTab(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);
    // Preserve scroll position per tab so swapping back to a tab returns the
    // user to where they were instead of the top. Two parts:
    //   1. A scroll listener continuously stamps the CURRENT tab's scrollTop
    //      into the ref. Reading scrollTop inside the tab-change effect is too
    //      late — the new tab's content has already replaced the layout.
    //   2. The activeTab effect restores the new tab's saved scrollTop.
    const scrollByTabRef = useRef({});
    const activeTabRef = useRef('players');
    useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
    useEffect(() => {
        const scroller = document.querySelector('.page-content');
        if (!scroller) return;
        const onScroll = () => {
            scrollByTabRef.current[activeTabRef.current] = scroller.scrollTop;
        };
        scroller.addEventListener('scroll', onScroll, { passive: true });
        return () => scroller.removeEventListener('scroll', onScroll);
    }, []);
    useEffect(() => {
        const scroller = document.querySelector('.page-content');
        if (!scroller) return;
        const target = scrollByTabRef.current[activeTab] ?? 0;
        // rAF so the new tab's content has rendered before we scroll. A second
        // rAF in case the first fires before sticky / layout settles.
        requestAnimationFrame(() => {
            scroller.scrollTo({ top: target, behavior: 'instant' });
            requestAnimationFrame(() => {
                if (Math.abs(scroller.scrollTop - target) > 2) {
                    scroller.scrollTo({ top: target, behavior: 'instant' });
                }
            });
        });
    }, [activeTab]);
    // "View In" menu (mirrors V1 rules)
    const [viewMenuOpen, setViewMenuOpen] = useState(false);
    const viewMenuRef = useRef(null);
    useEffect(() => {
        if (!viewMenuOpen) return;
        const handler = (e) => {
            if (viewMenuRef.current && !viewMenuRef.current.contains(e.target)) {
                setViewMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [viewMenuOpen]);

    // "Tools" menu (parity with V1 OrderDetail)
    const [toolsMenuOpen, setToolsMenuOpen] = useState(false);
    const toolsMenuRef = useRef(null);
    useEffect(() => {
        if (!toolsMenuOpen) return;
        const handler = (e) => {
            if (toolsMenuRef.current && !toolsMenuRef.current.contains(e.target)) setToolsMenuOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [toolsMenuOpen]);

    // Change Pickup Location modal
    const [showPickupLocModal, setShowPickupLocModal] = useState(false);
    const [showRefundModal,    setShowRefundModal]    = useState(false);
    const [reportIssueOpen,    setReportIssueOpen]    = useState(false);  // 2026-09-11: parity with V1
    const [pickupLocList,      setPickupLocList]      = useState([]);
    const [pickupLocSelected,  setPickupLocSelected]  = useState('');
    const [pickupLocLoading,   setPickupLocLoading]   = useState(false);
    const [pickupLocSaving,    setPickupLocSaving]    = useState(false);
    const [pickupLocError,     setPickupLocError]     = useState(null);

    // Tracks the most recent customer-email copy gesture so the "Copied"
    // badge can clear itself after ~1.8s. Same pattern as V1 OrderDetail.
    const [emailCopiedAt, setEmailCopiedAt] = useState(0);

    // Modify Picked & Pressed mode (button next to Expand Players).
    // pendingChanges: Map<`${groupKey}::${stage}`, { groupKey, stage, qty, desired }>
    const [modifyMode, setModifyMode] = useState(false);
    const [pendingChanges, setPendingChanges] = useState(new Map());
    const [pendingSubmitting, setPendingSubmitting] = useState(false);

    const [density, setDensity] = useState('default');
    const [ffSortMode, setFfSortMode] = useState('order');   // V1's per-FO "Sort by" (Order / Product / Player)
    const [expandAllNonce, setExpandAllNonce] = useState(0);
    const [expandPlayersNonce, setExpandPlayersNonce] = useState(0);
    const [compressAllNonce, setCompressAllNonce] = useState(0);
    // Players-tab stage cycles: collapsed → players-only → all → collapsed
    const [playersStage, setPlayersStage] = useState('collapsed');
    // Fulfillments-tab is a simple binary toggle.
    const [ffExpanded, setFfExpanded] = useState(false);
    // Fulfillments-tab filter: 'all' | 'fulfilled' | 'unfulfilled'
    const [ffFilter, setFfFilter] = useState('all');
    const [activePlayerKey, setActivePlayerKey] = useState(null);
    const [activeFoKey, setActiveFoKey] = useState(null);

    // Print pipeline state (mirrors V1 OrderDetail). Hidden OrderPrintCard
    // renders below; effect captures innerHTML once images settle, writes to
    // popup, tags as printed, logs event.
    const [printReady, setPrintReady]         = useState(false);
    const [printBins, setPrintBins]           = useState({});
    const [printOverstockBins, setPrintOverstockBins] = useState({});
    const [printPreviews, setPrintPreviews]   = useState({});
    const [showPrintConfirm, setShowPrintConfirm] = useState(false);
    const [printFull, setPrintFull] = useState(false);   // 2026-09-25: reprint every line, not just unfulfilled
    // 2026-09-26 (Eric): 'Merge NJ Warehouses' — see V1; every print of an eligible order goes through the Print order modal.
    const [showPrintOptions, setShowPrintOptions] = useState(false);
    const [mergeNj, setMergeNjState] = useState(() => readMergeNjPref());
    const setMergeNj = (on) => { setMergeNjState(on); writeMergeNjPref(on); };
    const [printMergedLocs, setPrintMergedLocs] = useState(null);
    const [showOnHoldBlock, setShowOnHoldBlock]   = useState(false);
    const [printToast, setPrintToast]         = useState(null);
    const printPopupRef  = useRef(null);
    const printSourceRef = useRef(null);

    const backToHref = useMemo(() => {
        let cached = null;
        try { cached = sessionStorage.getItem('ordersBackTo'); } catch {}
        return resolveOrdersBackTo(cached, activeStore || storeSlug, emulating);
    }, [activeStore, storeSlug, emulating]);

    // Load order + portal state. Keyed on the order id ONLY (2026-09-16, Eric):
    // picking another store in the sidebar while reading an order must not
    // reload or flash the page — the order stays as loaded; only the Orders link
    // and "Back to Orders" move to the newly chosen store.
    useEffect(() => {
        let cancelled = false;
        const fetchAtLoad = _storeFetchRef.current;
        (async () => {
            setLoading(true); setError(null);
            try {
                // useAuthFetch already prefixes ?store=<activeStore>; this
                // matches the pattern OrderDetail (V1) uses to load orders.
                const r = await fetchAtLoad(`/api/orders/${orderId}`);
                if (!r.ok) throw new Error('Order not found');
                const data = await r.json();
                if (cancelled) return;
                setOrder(data);
                const ps = await fetchAtLoad(`/api/orders/${orderId}/portal-state`)
                    .then(x => x.ok ? x.json() : {})
                    .catch(() => ({}));
                if (!cancelled) setPortalState(ps || {});
            } catch (e) {
                if (!cancelled) setError(e.message || 'Failed to load order');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Lightweight refresh used after notification create/delete — re-fetches
    // the full order payload so active_notifications reflects the new state.
    const refreshOrder = useCallback(async () => {
        try {
            const r = await _storeFetch(`/api/orders/${orderId}`);
            if (!r.ok) return;
            const data = await r.json();
            setOrder(data);
        } catch { /* ignore */ }
    }, [_storeFetch, orderId]);

    // Refresh portal-state (the pick/press/pack item_state). Called after the
    // pickup-loc override saves, after a Tools-menu reset, and after applying
    // pending Modify-mode changes.
    const refreshPortalState = useCallback(async () => {
        try {
            const r = await _storeFetch(`/api/orders/${orderId}/portal-state`);
            if (!r.ok) return;
            const data = await r.json();
            setPortalState(data || {});
        } catch { /* ignore */ }
    }, [_storeFetch, orderId]);

    // ── Tools menu handlers ─────────────────────────────────────────────────
    const openPickupLocModal = useCallback(async () => {
        setToolsMenuOpen(false);
        setPickupLocError(null);
        setPickupLocSelected(order?.store_pickup_override || '');
        setShowPickupLocModal(true);
        setPickupLocLoading(true);
        try {
            const res = await _storeFetch('/api/store-pickup-locations');
            const data = await res.json();
            setPickupLocList(data.locations || []);
        } catch (e) {
            setPickupLocError('Failed to load location list: ' + e.message);
        } finally {
            setPickupLocLoading(false);
        }
    }, [order, _storeFetch]);

    const handlePickupLocSave = useCallback(async () => {
        if (!pickupLocSelected) return;
        setPickupLocSaving(true); setPickupLocError(null);
        try {
            const res = await _storeFetch(`/api/orders/${orderId}/store-pickup-override`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ location: pickupLocSelected }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.detail || `HTTP ${res.status}`);
            }
            setShowPickupLocModal(false);
            await refreshOrder();
        } catch (e) { setPickupLocError(e.message); }
        finally { setPickupLocSaving(false); }
    }, [orderId, pickupLocSelected, _storeFetch, refreshOrder]);

    const handlePickupLocClear = useCallback(async () => {
        setPickupLocSaving(true); setPickupLocError(null);
        try {
            const res = await _storeFetch(`/api/orders/${orderId}/store-pickup-override`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ clear: true }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.detail || `HTTP ${res.status}`);
            }
            setShowPickupLocModal(false);
            await refreshOrder();
        } catch (e) { setPickupLocError(e.message); }
        finally { setPickupLocSaving(false); }
    }, [orderId, _storeFetch, refreshOrder]);

    const handleResync = useCallback(async () => {
        setToolsMenuOpen(false);
        try {
            const res = await _storeFetch(`/api/orders/${orderId}/resync`, { method: 'POST' });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                alert(`Resync failed: ${data.detail || res.status}`);
                return;
            }
            await refreshOrder();
        } catch (e) { alert(`Resync failed: ${e.message}`); }
    }, [orderId, _storeFetch, refreshOrder]);

    const handleToolAction = useCallback(async (action) => {
        setToolsMenuOpen(false);
        const confirmMessages = {
            reset_picking:  'Reset all picking history for this order? This cannot be undone.',
            reset_pressing: 'Reset all pressing history for this order? This cannot be undone.',
            reset_packing:  'Reset all packing history for this order? This cannot be undone.',
        };
        if (!window.confirm(confirmMessages[action])) return;
        const groups = Object.entries(portalState || {})
            .filter(([k]) => k !== '_pressing_summary')
            .map(([group_key, state]) => ({ group_key, qty: state.qty_total || 1 }));
        try {
            const res = await _storeFetch(`/api/pressing/orders/${orderId}/tools`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action, order_name: order?.name, groups }),
            });
            if (!res.ok) throw new Error('failed');
            await refreshPortalState();
        } catch { alert('Action failed. Please try again.'); }
    }, [orderId, order, portalState, _storeFetch, refreshPortalState]);

    // ── Modify Picked & Pressed handlers ────────────────────────────────────
    const enterModifyMode = useCallback(() => {
        setPendingChanges(new Map());
        setModifyMode(true);
    }, []);

    const cancelModify = useCallback(() => {
        setPendingChanges(new Map());
        setModifyMode(false);
    }, []);

    // Smart select-all toggle for one stage.
    // If every clickable row in this stage is currently effectively done
    // (current state OR a pending entry that takes it to done), the click
    // stages a flip to "not done" for every row that's actually done.
    // Otherwise, it stages a flip to "done" for every row that's actually not done.
    // Pending entries for OTHER stages are preserved untouched.
    const selectAllStage = useCallback((stage, targets) => {
        if (!Array.isArray(targets) || targets.length === 0) return;
        setPendingChanges(prev => {
            let effDone = 0;
            for (const t of targets) {
                const k = `${t.groupKey}::${stage}`;
                const p = prev.get(k);
                const isDoneEff = p ? p.desired : (t.currentState === 'done');
                if (isDoneEff) effDone++;
            }
            const desired = effDone < targets.length;
            const next = new Map();
            for (const [k, v] of prev) {
                if (!k.endsWith(`::${stage}`)) next.set(k, v);
            }
            for (const t of targets) {
                const actuallyDone = t.currentState === 'done';
                if (actuallyDone !== desired) {
                    next.set(`${t.groupKey}::${stage}`, {
                        groupKey: t.groupKey,
                        stage,
                        qty: t.qty || 1,
                        desired,
                    });
                }
            }
            return next;
        });
    }, []);

    // Click on a stage dot in modify mode. Toggles the pending desired state.
    // Click an unpicked dot → pending picked. Click again → no-op (remove).
    // Click a picked dot → pending unpicked. Click again → no-op.
    const togglePending = useCallback(({ groupKey, stage, qty, currentState, locId }) => {
        const key = `${groupKey}::${stage}`;
        setPendingChanges(prev => {
            const next = new Map(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                const desired = currentState === 'done' ? false : true;
                next.set(key, { groupKey, stage, qty: qty || 1, desired, locId: locId || '' });
            }
            return next;
        });
    }, []);

    const applyPending = useCallback(async () => {
        if (pendingChanges.size === 0 || pendingSubmitting) return;
        setPendingSubmitting(true);
        // One request per (action verb, FO location): the backend needs the
        // FO's shopify_location_id so events + item_state land at the right
        // location (it 400s without one — Mike Mara, 2026-09-20). A row whose
        // FO has no location falls back to the page's ?loc= location.
        const pageLoc = String(urlLocParam || '').trim();
        const buckets = new Map();   // `${verb}|${loc}` → { action, loc, groups }
        for (const { groupKey, stage, qty, desired, locId } of pendingChanges.values()) {
            const verb = stage === 'picked'
                ? (desired ? 'mark_selected_picked' : 'mark_selected_not_picked')
                : (desired ? 'mark_selected_pressed' : 'mark_selected_not_pressed');
            const loc = locId || pageLoc;
            if (!loc) {
                alert('This fulfillment has no assigned location — cannot mark items. Reload the order and try again.');
                setPendingSubmitting(false);
                return;
            }
            const k = `${verb}|${loc}`;
            if (!buckets.has(k)) buckets.set(k, { action: verb, loc, groups: [] });
            buckets.get(k).groups.push({ group_key: groupKey, qty, total_qty: qty });
        }
        try {
            for (const { action, loc, groups } of buckets.values()) {
                if (groups.length === 0) continue;
                const res = await _storeFetch(`/api/pressing/orders/${orderId}/tools`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action, order_name: order?.name, groups, shopify_location_id: loc }),
                });
                if (!res.ok) {
                    const j = await res.json().catch(() => ({}));
                    throw new Error(`${action} failed (HTTP ${res.status})${j.detail ? `: ${j.detail}` : ''}`);
                }
            }
            await refreshPortalState();
            setPendingChanges(new Map());
            setModifyMode(false);
        } catch (e) {
            alert(`Apply failed: ${e.message}`);
        } finally {
            setPendingSubmitting(false);
        }
    }, [pendingChanges, pendingSubmitting, orderId, order, _storeFetch, refreshPortalState, urlLocParam]);

    // Build bundles + price lookup
    const lineItems = useMemo(() => {
        const edges = order?.lineItems?.edges;
        if (Array.isArray(edges)) return edges.map(e => e.node).filter(Boolean);
        if (Array.isArray(order?.lineItems)) return order.lineItems;
        return [];
    }, [order]);

    const bundles = useMemo(() => {
        const built = buildBundles(lineItems, { tags: order?.tags });
        // Broken Orders (2026-09-16, Eric): the embellishments are POOLED lines
        // (e.g. "CFCB1 × 21") that the builder attaches to every garment that
        // requires them, so each garment showed the whole pool's quantity. V1
        // renders one per garment (childBundleQty = 1 on Broken Orders); do the
        // same here so the row qty, the dot totals and the rollups read per garment.
        const isBroken = (order?.tags || []).some(t => (t || '').trim().toLowerCase() === 'broken order');
        if (!isBroken) return built;
        return built.map(b => ({
            ...b,
            children: (b.children || []).map(c => (c && (c.quantity || 0) > 1 ? { ...c, quantity: 1, currentQuantity: 1, _pooledQuantity: c.quantity } : c)),
        }));
    }, [lineItems, order?.tags]);
    const priceOf = useCallback((id) => {
        const li = lineItems.find(x => x.id === id);
        if (!li) return 0;
        const p = li.discountedTotalSet?.shopMoney?.amount
            ?? li.originalTotalSet?.shopMoney?.amount
            ?? li.discountedTotal?.amount
            ?? li.originalTotal?.amount
            ?? (li.variant?.price && (parseFloat(li.variant.price) * (li.quantity || 1)));
        const num = parseFloat(p);
        return isFinite(num) ? num : 0;
    }, [lineItems]);

    const roster = useMemo(() => parseRoster(order), [order]);
    // Team Portal number / team overrides (V1 parity, Eric 2026-09-16). Fresh
    // from TP on every order load (order.tp_number_overrides / tp_team_overrides),
    // keyed by player id first, player name as fallback.
    const tpOverrides = useMemo(() => {
        const numById = {}, numByName = {}, teamById = {}, teamByName = {};
        for (const ov of (order?.tp_number_overrides || [])) {
            if (!ov) continue;
            if (ov.player_id != null) numById[String(ov.player_id)] = ov;
            if (ov.player_name) numByName[String(ov.player_name).trim().toLowerCase()] = ov;
        }
        for (const ov of (order?.tp_team_overrides || [])) {
            if (!ov) continue;
            if (ov.player_id != null) teamById[String(ov.player_id)] = ov;
            if (ov.player_name) teamByName[String(ov.player_name).trim().toLowerCase()] = ov;
        }
        const byPlayer = (playerId, name) => {
            const k = name ? String(name).trim().toLowerCase() : '';
            return {
                number: (playerId != null && numById[String(playerId)]) || (k && numByName[k]) || null,
                team:   (playerId != null && teamById[String(playerId)]) || (k && teamByName[k]) || null,
            };
        };
        const numberFor = (li) => {
            if (!li) return '';
            const p = getPlayer(li, roster);
            const ov = byPlayer(p.playerId, p.name).number;
            return ov ? String(ov.override_number) : '';
        };
        const any = Object.keys(numById).length + Object.keys(numByName).length + Object.keys(teamById).length + Object.keys(teamByName).length;
        return any ? { byPlayer, numberFor } : null;
    }, [order, roster]);
    const { playerGroups, retailBundles } = useMemo(() => {
        const { playerBundles, retailBundles } = splitBundlesByCategory(bundles, roster);
        return { playerGroups: groupByPlayer(playerBundles, roster, tpOverrides), retailBundles };
    }, [bundles, roster, tpOverrides]);
    const subSections = useMemo(() => buildFoSubSections(order), [order]);
    // Split-bundle detection — verbatim port of V1 (items of one bundle spread
    // across several fulfillment orders get a color-coded badge).
    const { splitBundleMap, hasSplitBundles } = useMemo(() => {
        const fosAll = (order?.fulfillmentOrders?.edges || []).map(e => e.node).filter(Boolean);
        const liToFoId = {};
        fosAll.forEach(fo => (fo.lineItems?.edges || []).forEach(e => { const id = e.node?.lineItem?.id; if (id) liToFoId[id] = fo.id; }));
        const groupFoSets = {};
        lineItems.forEach(li => {
            const g = li.lineItemGroup?.id; if (!g) return;
            const f = liToFoId[li.id]; if (!f) return;
            (groupFoSets[g] = groupFoSets[g] || new Set()).add(f);
        });
        const attrOf = (li, key) => (li.customAttributes || []).find(a => a.key === key)?.value;
        const parentByKey = {};
        lineItems.forEach(li => { const sk = attrOf(li, '_soccer_key'); if (sk) parentByKey[sk] = li; });
        const addonGroupKey = {};
        lineItems.forEach(li => {
            if (li.lineItemGroup?.id) return;
            const pk = attrOf(li, '_soccer_parent_line_item_key');
            const parent = pk ? parentByKey[pk] : null;
            if (!parent || parent.id === li.id) return;
            const fc = liToFoId[li.id], fp = liToFoId[parent.id];
            if (!fc || !fp) return;
            const native = parent.lineItemGroup?.id;
            const grpId = native || `tpkey:${parent.id}`;
            (groupFoSets[grpId] = groupFoSets[grpId] || new Set()).add(fp);
            groupFoSets[grpId].add(fc);
            addonGroupKey[li.id] = grpId;
            if (!native) addonGroupKey[parent.id] = grpId;
        });
        // 2026-09-25 (Eric): Advanced Configuration → "Ignore Teamatical products
        // in Jumbled Bundles". A bundle whose product carries
        // Teamatical_v1.teamaticalID is split by design (garment from Teamatical
        // Dropship, number child embellished locally); the backend flags it and
        // exposes the switch on the order payload.
        if (order?.jumbled_ignore_teamatical) {
            const tmGroups = new Set();
            lineItems.forEach(li => {
                const v = li.product?.teamaticalId?.value ?? li.variant?.product?.teamaticalId?.value;
                if (!v || !String(v).trim()) return;
                const g = li.lineItemGroup?.id || addonGroupKey[li.id];
                if (g) tmGroups.add(g);
            });
            tmGroups.forEach(g => { delete groupFoSets[g]; });
        }
        const map = {}; let idx = 0;
        Object.entries(groupFoSets).forEach(([g, set]) => { if (set.size > 1) { map[g] = idx % 6; idx++; } });
        Object.entries(addonGroupKey).forEach(([liId, g]) => { if (map[g] != null) map[liId] = map[g]; });
        return { splitBundleMap: map, hasSplitBundles: idx > 0 };
    }, [order, lineItems]);
    // Pickup order? (V1 rule) — override wins; else any pickup FO's store.
    const overrideLoc = (order?.store_pickup_override || '').trim() || null;
    const pickupLocations = overrideLoc ? [overrideLoc] : [...new Set(
        (order?.fulfillmentOrders?.edges || []).map(e => e.node).filter(Boolean)
            .filter(fo => isPickupMethod(fo.deliveryMethod?.methodType))
            .map(fo => fo.deliveryMethod?.presentedName || fo.assignedLocation?.name)
            .filter(Boolean))];
    const isPickupOrder = !!overrideLoc || (!order?.shippingAddress && pickupLocations.length > 0);
    // Everything the per-FO action menu needs (read by FoSubHeaderRow via context).
    const foMenuCtx = useMemo(() => ({
        orderId,
        storeKey: order?._store_key || activeStore || storeSlug || 'soccerpost',
        storeFetch: _storeFetch,
        storedLabels,
        storePickupOverride: order?.store_pickup_override || '',
        siblings: (order?.fulfillmentOrders?.edges || []).map(e => e.node).filter(Boolean).map(sib => ({
            id: sib.id, status: sib.status,
            locationName: sib.assignedLocation?.name || '',
            locationId: (sib.assignedLocation?.location?.id || '').split('/').pop() || '',
        })),
        onAfterAction: async () => { await refreshOrder(); await refreshPortalState(); setLabelsNonce(n => n + 1); },
        onToast: (t) => setPrintToast(t),
    }), [orderId, order, activeStore, storeSlug, _storeFetch, storedLabels, refreshOrder, refreshPortalState]);
    // Filtered view for the Fulfillments tab — 'fulfilled' = shipped sub-sections;
    // 'unfulfilled' = remaining-qty sub-sections. Legacy fallbacks stay in 'all'.
    const ffSubSections = useMemo(() => {
        if (ffFilter === 'all') return subSections;
        if (ffFilter === 'fulfilled') return subSections.filter(s => isSubSectionDone(s));
        if (ffFilter === 'unfulfilled') return subSections.filter(s => s.displayMode === 'pending' && !isSubSectionDone(s));
        return subSections;
    }, [subSections, ffFilter]);
    useEffect(() => {
        if (!activePlayerKey && playerGroups.length > 0) {
            setActivePlayerKey(playerGroups[0].key);
        }
    }, [playerGroups, activePlayerKey]);

    // ── Print pipeline ──────────────────────────────────────────────────────
    // Matches V1 OrderDetail handlePrint exactly: fetch bin labels + number
    // previews, open popup, capture hidden <OrderPrintCard> once images load,
    // POST printed_at_location, log timeline event.
    const isAdminOD = hasRole(user, ['admin']) || hasRole(user, ['customer_service']);
    const odPrintLocationId = (() => {
        if (emulating?.shopify_location_id) return String(emulating.shopify_location_id);
        if (isAdminOD) return null;
        const myLocsForStore = (user?.locations || [])
            .filter(l => (l.store_key || 'soccerpost') === (activeStore || 'soccerpost')
                      && l.shopify_location_id && String(l.shopify_location_id) !== '0');
        if (myLocsForStore.length === 1) return String(myLocsForStore[0].shopify_location_id);
        return null;
    })();
    const odPrintLocationInScope = isAdminOD
        ? !!emulating?.shopify_location_id
        : !!odPrintLocationId;
    const isOrderPrinted = !!order && (
        isPrintedTag(order?.tags) || getPerLocationPrintTags(order?.tags).length > 0
    );

    const fetchBins = useCallback(async () => {
        // Scope to the viewing location so multi-location orders resolve to
        // the user's view, not whichever FO Shopify returns first.
        const sloc = odPrintLocationId || '';
        const url = sloc
            ? `/api/orders/${orderId}/bin-locations?shopify_location_id=${encodeURIComponent(sloc)}`
            : `/api/orders/${orderId}/bin-locations`;
        try {
            const res = await _storeFetch(url);
            if (!res.ok) return { bins: {}, overstock: {} };
            const data = await res.json();
            return { bins: data.bins || {}, overstock: data.overstock || {} };
        } catch (_) { return { bins: {}, overstock: {} }; }
    }, [_storeFetch, orderId, odPrintLocationId]);

    const fetchPreviews = useCallback(async () => {
        try {
            const res = await _storeFetch(`/api/orders/${orderId}/number-previews`);
            if (!res.ok) return {};
            const data = await res.json();
            return data.previews || {};
        } catch (_) { return {}; }
    }, [_storeFetch, orderId]);

    const openLoadingPopup = useCallback(() => {
        const popup = window.open('', '', 'width=920,height=700,scrollbars=yes,resizable=yes');
        if (!popup) { alert('Please allow popups for this site to enable printing.'); return null; }
        writePopupHTML(popup, '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Loading…</title></head>\n<body style="font-family:sans-serif;padding:40px;color:#374151"><p style="font-size:15px">Loading…</p></body></html>');
        return popup;
    }, []);

    const njLocsOnOrder = useMemo(() => njWarehouseLocsOnOrder(order, { all: printFull }), [order, printFull]);
    const njOfferOD = njMergeAllowed(activeStore || storeSlug) && njLocsOnOrder.length === 2 && !!odPrintLocationId && njLocsOnOrder.some(l => String(l.id) === String(odPrintLocationId));
    const startPrint = useCallback((merge) => {
        setShowPrintOptions(false);
        const popup = openLoadingPopup();
        if (!popup) return;
        printPopupRef.current = popup;
        if (merge) {
            Promise.all([
                Promise.all(njLocsOnOrder.map(async l => {
                    try {
                        const r = await _storeFetch(`/api/orders/${orderId}/bin-locations?shopify_location_id=${encodeURIComponent(l.id)}`);
                        const j = r.ok ? await r.json() : {};
                        return { ...l, bins: j.bins || {}, overstockBins: j.overstock || {} };
                    } catch (_) { return { ...l, bins: {}, overstockBins: {} }; }
                })),
                fetchPreviews(),
            ]).then(([locs, previews]) => { setPrintMergedLocs(locs); setPrintPreviews(previews); setPrintReady(true); });
            return;
        }
        setPrintMergedLocs(null);
        Promise.all([fetchBins(), fetchPreviews()]).then(([binsResp, previews]) => {
            setPrintBins(binsResp.bins || {});
            setPrintOverstockBins(binsResp.overstock || {});
            setPrintPreviews(previews); setPrintReady(true);
        });
    }, [njLocsOnOrder, _storeFetch, orderId, fetchBins, fetchPreviews, openLoadingPopup]);

    const handlePrint = useCallback(() => {
        if (!odPrintLocationInScope) {
            setPrintToast({ msg: "You can't print orders when you are viewing \"All Locations\". Choose a single location first.", key: Date.now() });
            return;
        }
        // HARD BLOCK on held orders — release the hold in Shopify first.
        if (order?.displayFulfillmentStatus === 'ON_HOLD') {
            setShowOnHoldBlock(true);
            return;
        }
        if (njOfferOD) { setShowPrintOptions(true); return; }   // 2026-09-26: the Print order modal decides
        const alreadyTagged = isPrintedTag(order?.tags) || getPerLocationPrintTags(order?.tags).length > 0;
        if (alreadyTagged) {
            setShowPrintConfirm(true);
            return;
        }
        const popup = openLoadingPopup();
        if (!popup) return;
        printPopupRef.current = popup;
        Promise.all([fetchBins(), fetchPreviews()]).then(([binsResp, previews]) => {
            setPrintBins(binsResp.bins || {});
            setPrintOverstockBins(binsResp.overstock || {});
            setPrintPreviews(previews); setPrintReady(true);
        });
    }, [order?.tags, order?.displayFulfillmentStatus, fetchBins, fetchPreviews, odPrintLocationInScope, openLoadingPopup, njOfferOD]);

    const handlePrintConfirmed = useCallback(() => {
        setShowPrintConfirm(false);
        const popup = openLoadingPopup();
        if (!popup) return;
        printPopupRef.current = popup;
        Promise.all([fetchBins(), fetchPreviews()]).then(([binsResp, previews]) => {
            setPrintBins(binsResp.bins || {});
            setPrintOverstockBins(binsResp.overstock || {});
            setPrintPreviews(previews); setPrintReady(true);
        });
    }, [fetchBins, fetchPreviews, openLoadingPopup]);

    useEffect(() => {
        if (!printReady || !printSourceRef.current || !printPopupRef.current) return;
        const popup = printPopupRef.current;
        if (popup.closed) { setPrintReady(false); return; }
        let cancelled = false;
        const captureAndWrite = () => {
            if (cancelled) return;
            if (popup.closed) { setPrintReady(false); return; }
            const content = printSourceRef.current.innerHTML;
            writePopupHTML(popup, buildPopupHTML(content));
            const _printSloc = emulating?.shopify_location_id || order?._shopify_location_id || null;
            // 2026-09-26: a merged NJ ticket tags both warehouses (each had items on it).
            const _slocs = printMergedLocs ? printMergedLocs.map(l => l.id) : (_printSloc ? [_printSloc] : []);
            // One request for every warehouse on the ticket (2026-10-07, B2B-93748).
            (_slocs.length ? [_slocs] : []).reduce((chain, ids) => chain.then(() =>
                _storeFetch(`/api/orders/${orderId}/printed_at_location`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ shopify_location_id: String(ids[0]), shopify_location_ids: ids.map(String), action: 'add' }),
                }).then(r => r.ok ? r.json() : null).then(d => {
                    // Use the tags array the endpoint just wrote instead of re-fetching
                    // the order (the re-fetch could race Shopify's tag write).
                    if (d && Array.isArray(d.tags)) {
                        setOrder(prev => prev ? { ...prev, tags: d.tags } : prev);
                    }
                })), Promise.resolve()).catch(() => {});
            const alreadyPrinted = isPrintedTag(order?.tags);
            _storeFetch(`/api/orders/${orderId}/log-event`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    event_type: alreadyPrinted ? 'order_reprint' : 'order_print',
                    order_name: order?.name,
                    portal_username: user?.user || null,
                    payload: { full_order: !!printFull, ...(printMergedLocs ? { merged_nj_warehouses: true, locations: printMergedLocs.map(l => l.name) } : {}) },
                }),
            }).catch(() => {});
            setPrintReady(false);
            setPrintMergedLocs(null);
            printPopupRef.current = null;
        };
        const imgs = Array.from(printSourceRef.current.querySelectorAll('img'));
        const pending = imgs.filter(i => !i.complete || i.naturalWidth === 0);
        if (pending.length === 0) {
            requestAnimationFrame(() => requestAnimationFrame(captureAndWrite));
        } else {
            let left = pending.length;
            const tick = () => { if (--left <= 0) requestAnimationFrame(() => requestAnimationFrame(captureAndWrite)); };
            pending.forEach(img => {
                img.addEventListener('load',  tick, { once: true });
                img.addEventListener('error', tick, { once: true });
            });
            setTimeout(() => { if (!cancelled) captureAndWrite(); }, 2000);
        }
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [printReady, orderId]);

    // Auto-dismiss print toast after 4s.
    useEffect(() => {
        if (!printToast) return;
        const id = setTimeout(() => setPrintToast(null), 4000);
        return () => clearTimeout(id);
    }, [printToast]);

    // Measure sticky hero height so the panel toolbar / table head / player
    // header / sidebar can stick directly below it without gaps. Uses a
    // ResizeObserver and a poll fallback because the hero may not be in the
    // DOM on the first pass after `order` flips to truthy on some renders.
    useEffect(() => {
        const ros = [];
        let pollId;
        const targets = [
            ['.odv2-hero', '--odv2-hero-h'],
            ['.odv2-roster', '--odv2-roster-h'],
            ['.odv2-panel-toolbar', '--odv2-toolbar-h'],
            ['.odv2-table thead', '--odv2-thead-h'],
        ];
        const updateOne = (sel, varName) => {
            const el = document.querySelector(sel);
            const h = el ? el.offsetHeight : 0;
            document.documentElement.style.setProperty(varName, `${h}px`);
        };
        const updateAll = () => targets.forEach(([sel, v]) => updateOne(sel, v));
        const start = () => {
            const hero = document.querySelector('.odv2-hero');
            if (!hero) return false;
            updateAll();
            targets.forEach(([sel]) => {
                const el = document.querySelector(sel);
                if (el) {
                    const ro = new ResizeObserver(updateAll);
                    ro.observe(el);
                    ros.push(ro);
                }
            });
            window.addEventListener('resize', updateAll);
            return true;
        };
        if (!start()) {
            pollId = setInterval(() => { if (start()) clearInterval(pollId); }, 50);
        }
        return () => {
            ros.forEach(ro => ro.disconnect());
            if (pollId) clearInterval(pollId);
            window.removeEventListener('resize', updateAll);
        };
    }, [order]);

    // Backend per-line verdicts (app.py stamps node._lineItemFlags on every
    // FO node): line item id → {picked, packed, pressed}. Merged across FOs so
    // rows, bundle rollups and FO-header dots all read the same truth V1 does.
    const lineFlags = useMemo(() => {
        const out = {};
        for (const e of (order?.fulfillmentOrders?.edges || [])) {
            const flags = e?.node?._lineItemFlags;
            if (!flags) continue;
            for (const [lid, f] of Object.entries(flags)) {
                if (!out[lid]) { out[lid] = f; continue; }
                // Same line in two FOs (partial fulfilment): a positive verdict in either wins.
                out[lid] = {
                    picked:  !!(out[lid].picked  || f.picked),
                    packed:  !!(out[lid].packed  || f.packed),
                    pressed: !!(out[lid].pressed || f.pressed),
                };
            }
        }
        return out;
    }, [order]);
    // Team Portal per-line requirement classifications (V1's tpReqByLiId).
    const tpReqByLiId = useMemo(() => {
        const out = {};
        for (const l of (order?.tp_requirements?.line_items || [])) {
            if (l && l.line_item_gid) out[l.line_item_gid] = l;
        }
        return out;
    }, [order]);
    const fulfillmentShipped = !!(order && (
        order.displayFulfillmentStatus === 'FULFILLED'
        || order.displayFulfillmentStatus === 'PARTIALLY_FULFILLED'
        || order.fulfilled
        || order.fulfillmentStatus === 'fulfilled'
    ));
    // Set of line item IDs that appear in at least one non-cancelled SUCCESS
    // fulfillment. Used for the Fulfilled/Unfulfilled chip on virtual-item
    // rows — virtuals live in NONE-method FOs which buildFoSubSections drops,
    // so we can't lean on subSections for them.
    const shippedLineIds = useMemo(() => {
        const ids = new Set();
        for (const f of (order?.fulfillments || [])) {
            if ((f?.status || '').toUpperCase() === 'CANCELLED') continue;
            for (const e of (f?.fulfillmentLineItems?.edges || [])) {
                const id = e?.node?.lineItem?.id;
                if (id) ids.add(id);
            }
        }
        return ids;
    }, [order]);
    const isPartiallyFulfilled = order?.displayFulfillmentStatus === 'PARTIALLY_FULFILLED';
    const isFullyFulfilled = !isPartiallyFulfilled && fulfillmentShipped;

    // Targets considered by the Modify-mode select-all dots. Picked targets
    // include every bundle parent and every decoration child that has a SKU.
    // Pressed targets are only bundle parents WITH at least one decoration
    // child (matches the existing per-row pressed gating). Virtual line items
    // (Free Returns, Processing Fee) are skipped because they're filtered out
    // of the visible table by splitBundlesByCategory — including them would
    // make the big dot show "partial" even when every visible row is done.
    const selectAllTargets = useMemo(() => {
        if (!modifyMode) return { picked: [], pressed: [] };
        const picked = [];
        const pressed = [];
        for (const b of bundles) {
            const pgk = b.parent?.sku;
            if (!pgk) continue;
            if (isVirtual(b.parent?.title)) continue;
            const hasDeco = (b.children || []).some(c => isDecoration(c));
            const dots = dotsForBundle(b.parent, b.children, portalState, fulfillmentShipped, lineFlags);
            picked.push({ groupKey: pgk, qty: b.parent.quantity || 1, currentState: dots.picked.state });
            if (hasDeco) {
                pressed.push({ groupKey: pgk, qty: b.parent.quantity || 1, currentState: dots.pressed.state });
            }
            for (const c of (b.children || [])) {
                if (!isDecoration(c) || !c.sku) continue;
                if (isVirtual(c.title)) continue;
                const cps = psForSku(portalState, c.sku) || portalState[c.sku] || null;
                // Picked: the embellishment's own line (BE flag wins). Pressed +
                // Packed: an embellishment is pressed onto / packed with its
                // parent garment, so it inherits the bundle's state (Eric, 2026-09-15).
                const cdots = dotsForRow('decoration', c, cps, fulfillmentShipped, lineFlags?.[c.id]);
                cdots.pressed = dots.pressed;
                cdots.packed  = dots.packed;
                picked.push({ groupKey: c.sku, qty: c.quantity || 1, currentState: cdots.picked.state });
            }
        }
        return { picked, pressed };
    }, [modifyMode, bundles, portalState, fulfillmentShipped]);

    // Effective state for each select-all dot, considering pending changes.
    const computeBigDotState = (targets, stage) => {
        if (!targets || targets.length === 0) return 'todo';
        let done = 0;
        for (const t of targets) {
            const p = pendingChanges.get(`${t.groupKey}::${stage}`);
            const isDoneEff = p ? p.desired : (t.currentState === 'done');
            if (isDoneEff) done++;
        }
        if (done === targets.length) return 'done';
        if (done === 0) return 'todo';
        return 'partial';
    };
    const bigPickedState = computeBigDotState(selectAllTargets.picked, 'picked');
    const bigPressedState = computeBigDotState(selectAllTargets.pressed, 'pressed');

    // Order-level money — Shopify wraps these in *Set.shopMoney.amount.
    const moneyOf = (set) => {
        const v = set?.shopMoney?.amount ?? set?.amount ?? set;
        const n = parseFloat(v);
        return isFinite(n) ? n : 0;
    };
    const totalPrice    = moneyOf(order?.totalPriceSet)    || parseFloat(order?.totalPrice || 0);
    const subtotalPrice = moneyOf(order?.subtotalPriceSet) || parseFloat(order?.subtotalPrice || 0);
    const shippingPrice = moneyOf(order?.totalShippingPriceSet);
    const taxPrice      = moneyOf(order?.totalTaxSet);

    // Picked / pressed rollups for the hero pills. SINGLE SOURCE OF TRUTH:
    // read from order._cache (populated by /api/orders/:id from
    // operations_<sk>_order_cache) so the chip on this page exactly matches
    // the row in the Orders LIST and the original OrderDetail. The legacy
    // line-item walk is kept as a safety net for orders served before the
    // cache is populated.
    const pickPressTotals = useMemo(() => {
        // Read from order._cache (populated by /api/orders/:id from
        // operations_<sk>_order_cache) so every surface — Orders LIST cells,
        // V2 hero pills, side panel — shows the same X / Y numbers. Local
        // fulfillment data fills in the shipped count.
        const c = order?._cache || {};
        const itemsCount = Math.max(0, parseInt(c.items_count ?? 0, 10) || 0);
        const itemsPicked = Math.max(0, parseInt(c.items_picked_total ?? 0, 10) || 0);
        const itemsPacked = Math.max(0, parseInt(c.items_packed_total ?? 0, 10) || 0);
        const totalBundles = Math.max(0, parseInt(c.total_bundles ?? 0, 10) || 0);
        const itemsPressed = Math.max(0, parseInt(c.items_pressed_total ?? 0, 10) || 0);
        // Pack denominator (V1 rule): the server's total_pack_units — a bundle
        // packs as ONE unit, a standalone item packs per quantity, embellishment
        // lines are never their own pack unit. Fall back to the same client-side
        // count from the grouped bundles when the cache row predates the column.
        let totalPackUnits = Math.max(0, parseInt(c.total_pack_units ?? 0, 10) || 0);
        if (!totalPackUnits) {
            try {
                for (const b of (bundles || [])) {
                    const it = b?.parent || {};
                    if (isVirtual(it.title)) continue;
                    if ((b.children || []).length > 0) { totalPackUnits += 1; continue; }
                    if ((it.quantity || 0) === 0) continue;
                    if (isDecoration(it)) continue;
                    totalPackUnits += (it.quantity || 1);
                }
            } catch (_e) { /* fall through */ }
        }
        if (!totalPackUnits) totalPackUnits = itemsCount;
        // FULFILLED = done (V1 rule, 2026-08-06): when the order's effective
        // status is FULFILLED every unit left the building, so every numerator
        // equals its denominator regardless of what the aggregated cache says.
        const orderFullyShipped = (c.effective_fulfillment_status
                                    || order?.displayFulfillmentStatus
                                    || '').toUpperCase() === 'FULFILLED';
        const pickedNum  = orderFullyShipped && itemsCount > 0     ? itemsCount     : Math.min(itemsPicked, itemsCount);
        const packedNum  = orderFullyShipped && totalPackUnits > 0 ? totalPackUnits : Math.min(itemsPacked, totalPackUnits);
        const pressedNum = orderFullyShipped && totalBundles > 0   ? totalBundles   : Math.min(itemsPressed, totalBundles);
        // Packed is the terminal state — transferred to a store OR has a
        // tracking number OR FO fulfilled all count. We don't show a separate
        // "Shipped" stage because Shopify's fulfillment-row count under-reports
        // (misses transferred-out + orphan items that the cache correctly
        // counts as packed).
        return {
            pickedNum,
            pickedDen: itemsCount,
            packedNum,
            packedDen: totalPackUnits,
            pressedBundles: pressedNum,
            pressableBundles: totalBundles,
        };
    }, [order, bundles]);

    // ── render ──────────────────────────────────────────────────────────────
    if (loading) return <div className="odv2"><div className="odv2-loading">Loading…</div></div>;
    if (error)   return <div className="odv2"><div className="odv2-banner odv2-banner--err">{error}</div></div>;
    if (!order)  return null;

    const orderName = order.name || order.legacyResourceId || `SP-${orderId}`;
    const createdAt = order.createdAt ? new Date(order.createdAt) : null;
    // Match V1: prefer backend-computed _fulfill_by, fall back to the earliest FO.fulfillBy.
    const foEdges = order.fulfillmentOrders?.edges || [];
    const foFulfillBys = foEdges
        .map(e => e?.node?.fulfillBy)
        .filter(Boolean)
        .sort();
    const fulfillByRaw = order._fulfill_by || foFulfillBys[0] || null;
    // Timeline default zoom (Eric 2026-09-16): a bit before Order Placed to a bit
    // after Fulfill By (10% of that span on each side, at least 12 hours).
    const tlView = (() => {
        const startMs = new Date(order.processedAt || order.createdAt || Date.now()).getTime();
        const endMs = fulfillByRaw ? new Date(fulfillByRaw).getTime() : (Date.now() + 12 * 3600e3);
        const span = Math.max(endMs - startMs, 24 * 3600e3);
        const pad = Math.max(span * 0.1, 12 * 3600e3);
        return { start: startMs - pad, end: endMs + pad };
    })();
    const fulfillBy = fulfillByRaw ? new Date(fulfillByRaw) : null;
    const fmt = (d) => d ? d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

    const tabs = [
        // 2026-09-16 (Eric): an order with no Team Portal items (retail / Amazon /
        // storefront) calls the first tab "Items" instead of "Players".
        { id: 'players',       label: (playerGroups.length > 0 || (order.tags || []).includes('Team Portal')) ? 'Players' : 'Items',
                               icon: (playerGroups.length > 0 || (order.tags || []).includes('Team Portal')) ? '⚽' : '📦', count: playerGroups.length || null },
        { id: 'fulfillments',  label: 'Fulfillments',  icon: '🚚', count: null },
        { id: 'visual',        label: 'Visual Timeline', icon: '◷', count: null },
        { id: 'eventlog',      label: 'Order Event Log', icon: '≡', count: null },
    ];

    return (
        <div className={`odv2 ${density === 'compact' ? 'odv2--compact' : density === 'comfortable' ? 'odv2--comfortable' : density === 'maximum' ? 'odv2--comfortable odv2--maximum' : ''}`}>
            {/* Active broadcast notifications — stacked, no dismiss. Removal
                via the Notifications button below (order-scoped) or via
                Settings → Notifications. Pushes content down. */}
            {/* Missing required embellishments (2026-10-02) — approved by Eric as a warning on this page. */}
            <MissingEmbellishmentsBanner missing={order.missing_required} />

            {/* Cancelled banner (V1 markup) */}
            {order.cancelledAt && (
                <div className="od-cancelled-banner odv2-cancelled-banner">
                    <span className="od-cancelled-banner__icon">🚫</span>
                    <div>
                        <strong className="text-secondary-sm">This order has been cancelled</strong>
                        {order.cancelReason && (
                            <span className="od-cancelled-reason">
                                · Reason: {CANCEL_REASON_LABELS[order.cancelReason] || String(order.cancelReason).replace(/_/g, ' ').toLowerCase()}
                            </span>
                        )}
                        <span className="od-cancelled-at">{fmt(new Date(order.cancelledAt))}</span>
                    </div>
                </div>
            )}
            {/* Presence — who else has this order open. Fixed top-right so it
                stays in view while scrolling (2026-09-15, Eric). */}
            {viewers.length > 0 && (
                <div className="od-presence odv2-presence">
                    {viewers.map(v => (
                        <div key={v.user_id}
                             className={`od-presence-circle${v.is_me ? ' od-presence-circle--me' : ''}`}
                             style={{ background: presenceColor(v.user_id) }}
                             title={v.display_name}>
                            {v.initials}
                            {v.avatar_url && (
                                <>
                                    <img className="od-presence-avatar" src={v.avatar_url} alt="" draggable={false}
                                         onError={e => { e.currentTarget.parentElement.classList.add('od-presence-circle--noimg'); }} />
                                    <span className="od-presence-badge" aria-hidden>{v.initials}</span>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Hero */}
            <div className="odv2-hero">
                <a className="odv2-hero-back" href={backToHref}
                   onClick={(e) => { e.preventDefault(); navigate(backToHref); }}>
                    {ordersBackToLabel(activeStore || storeSlug, emulating, _storeNames)}
                </a>
                <div className="odv2-hero-row">
                    {order.replacement_alias ? (
                        /* Team Portal replacement order (V1 rule): raw name crossed
                           out and linking to the cancelled parent, alias + "(Order
                           Edited)" in red. */
                        <span className="odv2-hero-id">
                            {order.name !== order.replacement_alias && (
                                <Link to={`/store/${order?._store_key || activeStore || storeSlug || 'soccerpost'}/orders/${order.replacement_parent_order_id}`}
                                      style={{ textDecoration: 'line-through', opacity: 0.55, color: 'inherit', marginRight: 10 }}
                                      title={`Open the original (cancelled) order ${order.replacement_parent_name || ''}`}>
                                    {order.name}
                                </Link>
                            )}
                            <span style={{ color: '#c1281c' }}>{order.replacement_alias}</span>
                            <span style={{ color: '#c1281c', fontSize: '0.55em', fontWeight: 600, marginLeft: 10 }}>(Order Edited)</span>
                        </span>
                    ) : (
                        <span className="odv2-hero-id">{orderName}</span>
                    )}
                    <span className="odv2-hero-meta">
                        {fmt(createdAt)} · Fulfill by <span className="odv2-hero-fulfillby">{fmt(fulfillBy)}</span>
                    </span>
                </div>
                {(() => {
                    const clubs = Array.from(new Set(playerGroups.map(pg => pg.club).filter(Boolean)));
                    if (clubs.length === 0) return null;
                    return (
                        <div className="odv2-hero-clubs">
                            {clubs.map(c => <span key={c} className="odv2-club odv2-club--hero">{c}</span>)}
                        </div>
                    );
                })()}
                <div className="odv2-hero-pills">
                    {/* status: dot + label (no pill background) */}
                    <span className="odv2-status">
                        <span className={`odv2-dot ${isFullyFulfilled ? 'odv2-dot--ok' : 'odv2-dot--warn'}`} />
                        {isFullyFulfilled ? 'Fulfilled' : isPartiallyFulfilled ? 'Partially Fulfilled' : 'Unfulfilled'}
                    </span>
                    {order.displayFinancialStatus && (
                        <span className="odv2-status">
                            <span className="odv2-dot odv2-dot--ok" />
                            {String(order.displayFinancialStatus)
                                .replace(/_/g, ' ')
                                .toLowerCase()
                                .replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                    )}
                    {/* inline metrics: label tightly attached to value */}
                    <span className="odv2-metric"><span className="odv2-metric-label">Picked</span><span className={`odv2-metric-val ${pickPressTotals.pickedNum >= pickPressTotals.pickedDen && pickPressTotals.pickedDen > 0 ? 'odv2-metric-val--ok' : ''}`}>{pickPressTotals.pickedNum}/{pickPressTotals.pickedDen}</span></span>
                    {pickPressTotals.pressableBundles > 0 && (
                        <span className="odv2-metric"><span className="odv2-metric-label">Pressed</span><span className={`odv2-metric-val ${pickPressTotals.pressedBundles >= pickPressTotals.pressableBundles ? 'odv2-metric-val--ok' : ''}`}>{pickPressTotals.pressedBundles}/{pickPressTotals.pressableBundles}</span></span>
                    )}
                    <span className="odv2-metric"><span className="odv2-metric-label">Packed</span><span className={`odv2-metric-val ${pickPressTotals.packedNum >= pickPressTotals.packedDen && pickPressTotals.packedDen > 0 ? 'odv2-metric-val--ok' : ''}`}>{pickPressTotals.packedNum}/{pickPressTotals.packedDen}</span></span>
                </div>
                {/* Hero right-column: compact Customer + Ship To. Replaces the
                    previous floating TAGS box (tags moved to the sidebar). */}
                <div className="odv2-hero-meta-card">
                    <div className="odv2-hero-meta-block">
                        <div className="odv2-hero-meta-label">
                            <svg className="odv2-hero-meta-icon" width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                <circle cx="10" cy="7" r="3.2" stroke="currentColor" strokeWidth="1.5"/>
                                <path d="M3.5 17c1.2-3.2 3.7-5 6.5-5s5.3 1.8 6.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                            Customer
                        </div>
                        <div className="odv2-hero-meta-name">
                            {order.customer?.displayName || `${order.customer?.firstName || ''} ${order.customer?.lastName || ''}`.trim() || '—'}
                        </div>
                        {(order.customer?.email || order.email) && (
                            <div className="odv2-hero-meta-sub odv2-hero-meta-link od-copyable">
                                <span className="od-copyable__text">{order.customer?.email || order.email}</span>
                                <button
                                    type="button"
                                    className="od-copy-btn"
                                    aria-label="Copy email to clipboard"
                                    title="Copy email"
                                    onClick={async () => {
                                        const txt = order.customer?.email || order.email;
                                        if (!txt) return;
                                        try {
                                            if (navigator.clipboard?.writeText) {
                                                await navigator.clipboard.writeText(txt);
                                            } else {
                                                const ta = document.createElement('textarea');
                                                ta.value = txt;
                                                ta.style.position = 'fixed'; ta.style.opacity = '0';
                                                document.body.appendChild(ta);
                                                ta.select();
                                                document.execCommand('copy');
                                                document.body.removeChild(ta);
                                            }
                                            const stamp = Date.now();
                                            setEmailCopiedAt(stamp);
                                            setTimeout(() => {
                                                setEmailCopiedAt(prev => prev === stamp ? 0 : prev);
                                            }, 1800);
                                        } catch (_) { /* clipboard blocked — silently noop */ }
                                    }}
                                >
                                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                        <rect x="6" y="6" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.6"/>
                                        <path d="M3 13V5a2 2 0 012-2h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                                    </svg>
                                </button>
                                {emailCopiedAt > 0 && (
                                    <span key={emailCopiedAt} className="od-copied-badge">Copied</span>
                                )}
                            </div>
                        )}
                        {order.customer?.phone && (
                            <div className="odv2-hero-meta-sub">{order.customer.phone}</div>
                        )}
                    </div>
                    {/* 2026-09-16 (Eric): a pickup override wins over the ship-to block —
                        the goods go to the override store, not the customer's address. */}
                    {order.shippingAddress && !overrideLoc ? (
                        <div className="odv2-hero-meta-block">
                            {order.shippingAddress.validationResultSummary && order.shippingAddress.validationResultSummary !== 'NO_ISSUES' && (
                                <button type="button" className="od-addr-issues odv2-addr-issues" onClick={openAddrReview}>
                                    ⚠ Review address issues
                                </button>
                            )}
                            <div className="odv2-hero-meta-label">
                                <svg className="odv2-hero-meta-icon" width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                    <path d="M2 5h9v8H2V5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                                    <path d="M11 8h4l3 3v2h-7V8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                                    <circle cx="6" cy="15" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
                                    <circle cx="14" cy="15" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                                Ship to
                            </div>
                            <div className="odv2-hero-meta-name">
                                {order.shippingAddress.name || `${order.shippingAddress.firstName || ''} ${order.shippingAddress.lastName || ''}`.trim()}
                            </div>
                            <div className="odv2-hero-meta-sub">
                                {order.shippingAddress.address1}
                                {order.shippingAddress.address2 ? `, ${order.shippingAddress.address2}` : ''}
                                {`, ${order.shippingAddress.city}, ${order.shippingAddress.provinceCode || order.shippingAddress.province || ''} ${order.shippingAddress.zip || ''}`}
                                {order.shippingAddress.countryCode ? ` · ${order.shippingAddress.countryCode}` : ''}
                            </div>
                        </div>
                    ) : isPickupOrder ? (
                        /* No shipping address: a pickup order — show the pickup store,
                           and the override in red when one is set (2026-09-15, Eric). */
                        <div className="odv2-hero-meta-block">
                            <div className="odv2-hero-meta-label">🏪 Pickup at</div>
                            {pickupLocations.map(loc => (
                                <div key={loc} className={`odv2-hero-meta-name${overrideLoc ? ' odv2-pickup-override' : ''}`}>
                                    📍 {loc}{overrideLoc ? <span className="odv2-pickup-override-tag">pickup override</span> : null}
                                    {overrideLoc && hasRole(user, ['admin', 'site_manager', 'user']) && (
                                        <span className="odv2-pickup-override-actions">
                                            <button type="button" className="odv2-linkbtn" onClick={openPickupLocModal}>Change</button>
                                            <span>·</span>
                                            <button type="button" className="odv2-linkbtn"
                                                    onClick={() => {
                                                        if (!window.confirm('Clear the pickup location override? The order will revert to its original pickup location.')) return;
                                                        handlePickupLocClear();
                                                    }}>Clear</button>
                                        </span>
                                    )}
                                </div>
                            ))}
                            {!overrideLoc && <div className="odv2-hero-meta-sub">Pickup order</div>}
                            {overrideLoc && order.shippingAddress && (
                                <div className="odv2-hero-meta-sub odv2-hero-meta-struck"
                                     title="The customer's ship-to address — overridden, goods go to the store above">
                                    Ship to: {order.shippingAddress.name || `${order.shippingAddress.firstName || ''} ${order.shippingAddress.lastName || ''}`.trim()}, {order.shippingAddress.address1}{order.shippingAddress.address2 ? `, ${order.shippingAddress.address2}` : ''}, {order.shippingAddress.city}, {order.shippingAddress.provinceCode || order.shippingAddress.province || ''} {order.shippingAddress.zip || ''}
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>
                <div className="odv2-hero-actions odv2-hero-actions--row">
                    {/* View In menu — V1 markup (od-* classes) for visual parity */}
                    <div className="od-view-menu" ref={viewMenuRef}>
                        <button className="od-action-btn"
                                onClick={() => setViewMenuOpen(v => !v)}
                                aria-expanded={viewMenuOpen}>
                            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M10 12a2 2 0 100-4 2 2 0 000 4zM4 12a2 2 0 100-4 2 2 0 000 4zM16 12a2 2 0 100-4 2 2 0 000 4z"/>
                            </svg>
                            View In
                            <svg className={`od-chevron${viewMenuOpen ? ' od-chevron--open' : ''}`} width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M5 7l5 5 5-5"/>
                            </svg>
                        </button>
                        {viewMenuOpen && (() => {
                            const sk = activeStore || storeSlug || 'soccerpost';
                            const portalRolesAllowed = !allRolesIn(user, ['crm', 'customer_service', 'finance', 'store_manager', 'external']);
                            const adminAllowed = hasRole(user, ['admin', 'site_manager', 'user', 'crm', 'customer_service', 'finance', 'store_manager', 'external']);
                            // Order may belong to a different store than activeStore (e.g. /store/all/... or
                            // emulating). Backend stamps the resolved store on _store_key — prefer it so the
                            // Shopify Admin link always lands on the order's actual store.
                            const adminHandle = SHOPIFY_ADMIN_HANDLES[order?._store_key || sk] || 'soccerpostshop';
                            return (
                                <div className="od-view-dropdown">
                                    {portalRolesAllowed && (
                                        <Link to={`/store/${sk}/picking-portal/orders/${orderId}`}
                                              state={{ returnTo: `/store/${sk}/orders/${orderId}?v=2` }}
                                              className="od-view-item"
                                              onClick={() => setViewMenuOpen(false)}>
                                            <span className="od-view-item__icon">
                                                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                                    <path d="M3 4h2l2.5 8h7l2-5H6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <circle cx="8.5" cy="15" r="1.2" fill="currentColor"/>
                                                    <circle cx="14.5" cy="15" r="1.2" fill="currentColor"/>
                                                </svg>
                                            </span>
                                            <span className="od-view-item__body">
                                                <span className="od-view-item__label">Picking Portal</span>
                                                <span className="od-view-item__sub">Pick items for this order</span>
                                            </span>
                                        </Link>
                                    )}
                                    {portalRolesAllowed && (
                                        <Link to={`/store/${sk}/pressing-portal/orders/${orderId}`}
                                              state={{ returnTo: `/store/${sk}/orders/${orderId}?v=2` }}
                                              className="od-view-item"
                                              onClick={() => setViewMenuOpen(false)}>
                                            <span className="od-view-item__icon">
                                                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                                    <path d="M3 6c0-1.1.9-2 2-2h10a2 2 0 012 2v2a4 4 0 010 8H3V6z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M7 12h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                                                </svg>
                                            </span>
                                            <span className="od-view-item__body">
                                                <span className="od-view-item__label">Pressing Portal</span>
                                                <span className="od-view-item__sub">Press this order</span>
                                            </span>
                                        </Link>
                                    )}
                                    {portalRolesAllowed && (
                                        <Link to={`/store/${sk}/packing-portal/orders/${orderId}`}
                                              state={{ returnTo: `/store/${sk}/orders/${orderId}?v=2` }}
                                              className="od-view-item"
                                              onClick={() => setViewMenuOpen(false)}>
                                            <span className="od-view-item__icon">
                                                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                                    <path d="M3 5a2 2 0 012-2h10a2 2 0 012 2v1H3V5z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M3 6h14v10a2 2 0 01-2 2H5a2 2 0 01-2-2V6z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M8 10h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                                                </svg>
                                            </span>
                                            <span className="od-view-item__body">
                                                <span className="od-view-item__label">Packing Portal</span>
                                                <span className="od-view-item__sub">Pack this order</span>
                                            </span>
                                        </Link>
                                    )}
                                    {adminAllowed && (
                                        <>
                                            {portalRolesAllowed && <div className="od-view-divider" />}
                                            <a href={`https://admin.shopify.com/store/${adminHandle}/orders/${orderId}`}
                                               target="_blank"
                                               rel="noopener noreferrer"
                                               className="od-view-item"
                                               onClick={() => setViewMenuOpen(false)}>
                                                <span className="od-view-item__icon od-view-item__icon--muted">
                                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                                        <path d="M11 3h6v6M17 3l-9 9M8 5H4a1 1 0 00-1 1v10a1 1 0 001 1h10a1 1 0 001-1v-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                </span>
                                                <span className="od-view-item__body">
                                                    <span className="od-view-item__label">Shopify Admin</span>
                                                    <span className="od-view-item__sub">Open in Shopify</span>
                                                </span>
                                            </a>
                                        </>
                                    )}
                                    {/* Order Edits (Soccer Post Production app) — same gate as V1
                                        (2026-08-31 spec): admin / franchise / site_manager / user /
                                        customer_service, Team Portal-capable store, Team Portal-sourced
                                        order. Franchises use the Team Portal manager view. 2026-09-15 (Eric). */}
                                    {hasRole(user, ['admin', 'franchise', 'site_manager', 'user', 'customer_service'])
                                        && ['soccerpost', 'surf', 'rush', 'citysports'].includes(order?._store_key || sk)
                                        && ['team_portal', 'tp_retail', 'tp_addon', 'tp_retail_addon'].includes(order?._cache?.source) && (
                                        <a href={allRolesIn(user, ['franchise'])
                                                    ? `https://app.soccerpost.com/manager/orders?id=${orderId}`
                                                    : `https://admin.shopify.com/store/${adminHandle}/apps/soccer-post-production/orders/${orderId}`}
                                           target="_blank"
                                           rel="noopener noreferrer"
                                           className="od-view-item od-view-item--order-edits"
                                           onClick={() => setViewMenuOpen(false)}>
                                            <span className="od-view-item__icon od-view-item__icon--muted">
                                                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                                    <path d="M13.5 3.5l3 3L7 16H4v-3l9.5-9.5z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M12 5l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                                                </svg>
                                            </span>
                                            <span className="od-view-item__body">
                                                <span className="od-view-item__label">Order Edits</span>
                                                <span className="od-view-item__sub" style={{ whiteSpace: 'nowrap' }}>Edit within Team Portal</span>
                                            </span>
                                        </a>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                    {/* Tools menu — V1 markup. Same gate as V1 (2026-09-11). */}
                    {!allRolesIn(user, ['crm', 'finance', 'store_manager', 'external']) && (
                    <div className="od-view-menu" ref={toolsMenuRef}>
                        <button className="od-action-btn"
                                onClick={() => setToolsMenuOpen(v => !v)}
                                aria-expanded={toolsMenuOpen}>
                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                                <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l.3-.3a2 2 0 10-2.8-2.8l-.5.1zM13 8l-9 9-1 3 3-1 9-9-2-2z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Tools
                            <svg className={`od-chevron${toolsMenuOpen ? ' od-chevron--open' : ''}`} width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M5 7l5 5 5-5"/>
                            </svg>
                        </button>
                        {toolsMenuOpen && (
                            <div className="od-view-dropdown">
                                {/* franchise added 2026-09-30 (Eric): the list they get is only their own locations. */}
                                {hasRole(user, ['admin', 'site_manager', 'user', 'customer_service', 'franchise']) && (
                                    <button type="button" className="od-view-item" onClick={openPickupLocModal}>
                                        <span className="od-view-item__icon od-view-item__icon--muted">
                                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M10 2a6 6 0 016 6c0 4-6 10-6 10S4 12 4 8a6 6 0 016-6z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><circle cx="10" cy="8" r="2" stroke="currentColor" strokeWidth="1.6"/></svg>
                                        </span>
                                        <span className="od-view-item__body">
                                            <span className="od-view-item__label" style={{ color: '#1e3a8a' }}>Change Pickup Location</span>
                                            <span className="od-view-item__sub">Override the pickup store for this order</span>
                                        </span>
                                    </button>
                                )}
                                {!allRolesIn(user, ['customer_service', 'store_manager']) && (<>
                                    <button type="button" className="od-view-item od-view-item--danger"
                                            onClick={() => handleToolAction('reset_picking')}>
                                        <span className="od-view-item__icon od-view-item__icon--muted">
                                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M4 6h12M8 6V4h4v2M7 6l1 10h4l1-10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        </span>
                                        <span className="od-view-item__body">
                                            <span className="od-view-item__label">Reset Picking History</span>
                                            <span className="od-view-item__sub">Clear all picked quantities</span>
                                        </span>
                                    </button>
                                    <button type="button" className="od-view-item od-view-item--danger"
                                            onClick={() => handleToolAction('reset_pressing')}>
                                        <span className="od-view-item__icon od-view-item__icon--muted">
                                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M4 6h12M8 6V4h4v2M7 6l1 10h4l1-10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        </span>
                                        <span className="od-view-item__body">
                                            <span className="od-view-item__label">Reset Pressing History</span>
                                            <span className="od-view-item__sub">Clear all pressed quantities</span>
                                        </span>
                                    </button>
                                    <button type="button" className="od-view-item od-view-item--danger"
                                            onClick={() => handleToolAction('reset_packing')}>
                                        <span className="od-view-item__icon od-view-item__icon--muted">
                                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M4 6h12M8 6V4h4v2M7 6l1 10h4l1-10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        </span>
                                        <span className="od-view-item__body">
                                            <span className="od-view-item__label">Reset Packing History</span>
                                            <span className="od-view-item__sub">Clear all QC pass/fail state</span>
                                        </span>
                                    </button>
                                </>)}
                                {hasRole(user, ['admin']) && (
                                    <button type="button" className="od-view-item" onClick={handleResync}>
                                        <span className="od-view-item__icon od-view-item__icon--muted">
                                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M4 10a6 6 0 0110.5-4M16 10a6 6 0 01-10.5 4M14 3v4h-4M6 17v-4h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        </span>
                                        <span className="od-view-item__body">
                                            <span className="od-view-item__label">Force Resync from Shopify</span>
                                            <span className="od-view-item__sub">Re-fetch FO/fulfillment state if the view looks stale</span>
                                        </span>
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    )}
                    <OrderNotificationsControl
                        storeKey={activeStore || storeSlug || 'soccerpost'}
                        orderId={orderId}
                        orderName={order?.name}
                        activeNotifications={order?.active_notifications}
                        onChange={refreshOrder}
                    />
                    {hasRole(user, ['admin', 'site_manager', 'customer_service', 'franchise', 'user']) && (
                        <button
                            onClick={() => setReportIssueOpen(true)}
                            className="od-action-btn"
                            title="Report a problem with this order"
                        >
                            ⚠ Report Issue
                        </button>
                    )}
                    {hasRole(user, ['admin', 'franchise', 'site_manager', 'user', 'customer_service', 'store_manager']) && (
                        <button
                            onClick={() => setShowRefundModal(true)}
                            className="od-action-btn"
                            title="Issue a refund for one or more items on this order"
                        >
                            {/* Same return-arrow icon used on V1, so the two
                              * pages read identically when an operator flips
                              * between them. */}
                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 4L3 10l6 6M3 10h13a3 3 0 010 6h-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Refund
                        </button>
                    )}
                    {!allRolesIn(user, ['crm', 'finance', 'customer_service', 'external']) && (
                    <button
                        onClick={handlePrint}
                        className={`od-action-btn od-action-btn--primary${!odPrintLocationInScope ? ' od-action-btn--disabled' : ''}`}
                        title={!odPrintLocationInScope ? "You can't print orders when you are viewing \"All Locations\". Choose a single location first." : undefined}
                    >
                        <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 7V3h10v4M5 15H3a1 1 0 01-1-1V9a1 1 0 011-1h14a1 1 0 011 1v5a1 1 0 01-1 1h-2M5 11h10v6H5v-6z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Print Order
                        {isOrderPrinted && <span className="od-print-badge">(printed)</span>}
                    </button>
                    )}
                </div>
                {/* Active broadcast notifications — below the buttons, above the
                    tabs (2026-09-15, Eric). */}
                {Array.isArray(order?.active_notifications) && order.active_notifications.length > 0 && (
                    <div className="odv2-hero-notif">
                        <OrderNotificationsBanner notifications={order.active_notifications} />
                    </div>
                )}
                {/* v3 stages strip slot */}
                {stagesStripFn && stagesStripFn({ pickPressTotals, isOrderPrinted })}
                <div className="odv2-tabs">
                    {tabs.map(t => (
                        <button key={t.id}
                                className={`odv2-tab ${activeTab === t.id ? 'odv2-tab--active' : ''}`}
                                onClick={() => setActiveTab(t.id)}>
                            <span className="odv2-tab-icon">{t.icon}</span>{t.label}
                            {t.count != null && <span className="odv2-tab-count">{t.count}</span>}
                        </button>
                    ))}
                </div>
            </div>

            {hasSplitBundles && (
                <div className="od-jumble-alert odv2-jumble-alert">
                    <span className="od-jumble-alert__icon">⚠️</span>
                    <div>
                        <span className="od-jumble-alert__title">JUMBLED BUNDLE ALERT</span>
                        <span className="od-jumble-alert__detail">
                            One or more bundles are split across shipments.  Color-coded badges mark the affected items.
                        </span>
                    </div>
                </div>
            )}

            {/* Roster / Fulfillments strip when there are multiple cards */}
            {activeTab === 'players' && (
                <RosterStrip playerGroups={playerGroups}
                             tpPlayers={order.tp_requirements?.players || []}
                             retailBundles={retailBundles}
                             activeKey={activePlayerKey}
                             onPick={setActivePlayerKey} />
            )}
            {activeTab === 'fulfillments' && (
                <FulfillmentsStrip subSections={subSections}
                                   bundles={bundles}
                                   activeKey={activeFoKey}
                                   onPick={(key) => {
                                       const hidden = !ffSubSections.some(s => s.key === key);
                                       if (hidden) setFfFilter('all');
                                       setActiveFoKey(key);
                                   }} />
            )}
            {/* 2026-09-16: the sticky strip spacer is gone — its z-index 150 band painted over the
                hero's View In / Tools dropdowns; the timeline wrap now isolates its own overlays. */}

            <StoreKeyContext.Provider value={order?._store_key || activeStore || storeSlug || 'soccerpost'}>
            <SkuNotificationsContext.Provider value={skuNotifications}>
            <TpOverridesContext.Provider value={tpOverrides}>
            <TpReqContext.Provider value={tpReqByLiId}>
            <LineFlagsContext.Provider value={lineFlags}>
            <JumbleContext.Provider value={splitBundleMap}>
            <NpContext.Provider value={numberPreviews}>
            <FoMenuContext.Provider value={foMenuCtx}>
            <div className={`odv2-shell${TL_TABS.has(activeTab) ? ' odv2-shell--timeline' : ''}`}>
                <div className="odv2-panel">
                    {!TL_TABS.has(activeTab) && (() => {
                        // "items" = sum of qty across ALL Shopify lineItems (incl.
                        // virtuals like processing fee). "bundles" = pressable
                        // bundles (cache.total_bundles). See feedback_bundles_definition.
                        const totalBundles = Math.max(0, parseInt(order?._cache?.total_bundles ?? 0, 10) || 0);
                        const qtyOf = (li) => parseInt(li?.currentQuantity ?? li?.quantity ?? 0, 10) || 0;
                        const totalItemQty = lineItems.reduce((s, li) => s + qtyOf(li), 0);
                        return (
                        <div className="odv2-panel-toolbar">
                        <span style={{ fontSize: 13, color: 'var(--odv2-muted)' }}>
                            {totalItemQty} item{totalItemQty !== 1 ? 's' : ''} · {totalBundles} bundle{totalBundles !== 1 ? 's' : ''}
                        </span>
                        <span className="odv2-toolbar-spacer" />
                        <div className="odv2-segctl">
                            {/* 2026-09-15 (Eric): Maximum = Comfortable spacing with 30% larger product images. */}
                            <button className={`odv2-segctl-btn ${density === 'maximum' ? 'odv2-segctl-btn--active' : ''}`}
                                    onClick={() => setDensity('maximum')}>Maximum</button>
                            <button className={`odv2-segctl-btn ${density === 'comfortable' ? 'odv2-segctl-btn--active' : ''}`}
                                    onClick={() => setDensity('comfortable')}>Comfortable</button>
                            <button className={`odv2-segctl-btn ${density === 'default' ? 'odv2-segctl-btn--active' : ''}`}
                                    onClick={() => setDensity('default')}>Default</button>
                            <button className={`odv2-segctl-btn ${density === 'compact' ? 'odv2-segctl-btn--active' : ''}`}
                                    onClick={() => setDensity('compact')}>Compact</button>
                        </div>
                        {activeTab === 'players' && (
                            <div className="odv2-segctl odv2-segctl--detached">
                                <button className="odv2-segctl-btn odv2-segctl-btn--action"
                                        onClick={() => {
                                            if (playersStage === 'collapsed') {
                                                setExpandPlayersNonce(n => n + 1);
                                                setPlayersStage('players');
                                            } else if (playersStage === 'players') {
                                                setExpandAllNonce(n => n + 1);
                                                setPlayersStage('all');
                                            } else {
                                                setCompressAllNonce(n => n + 1);
                                                setPlayersStage('collapsed');
                                            }
                                        }}>
                                    {playersStage === 'collapsed' ? 'Expand Players'
                                        : playersStage === 'players' ? 'Expand Bundles'
                                        : 'Compress All'}
                                </button>
                            </div>
                        )}
                        {(activeTab === 'players' || activeTab === 'fulfillments')
                            && !allRolesIn(user, ['crm', 'finance', 'customer_service', 'store_manager', 'external'])
                            && !modifyMode && (
                            <div className="odv2-segctl odv2-segctl--detached" style={{ marginLeft: 8 }}>
                                <Tooltip content="Click the dots to change picked/pressed status" preferredPosition="below">
                                    <button className="odv2-segctl-btn odv2-segctl-btn--action"
                                            onClick={enterModifyMode}>Mod. Pick/Press</button>
                                </Tooltip>
                            </div>
                        )}
                        {activeTab === 'fulfillments' && (
                            <div className="odv2-segctl odv2-segctl--detached" title="Sort the items inside each fulfillment">
                                {[['order', '📋 Order'], ['product', '📦 Product'], ['player', '👤 Player']].map(([m, l]) => (
                                    <button key={m} className={`odv2-segctl-btn odv2-segctl-btn--sort ${ffSortMode === m ? 'odv2-segctl-btn--active' : ''}`}
                                            onClick={() => setFfSortMode(m)}>{l}</button>
                                ))}
                            </div>
                        )}
                        {activeTab === 'fulfillments' && (
                            <div className="odv2-segctl odv2-segctl--detached">
                                <button className="odv2-segctl-btn odv2-segctl-btn--action"
                                        onClick={() => {
                                            if (ffExpanded) {
                                                setCompressAllNonce(n => n + 1);
                                                setFfExpanded(false);
                                            } else {
                                                setExpandAllNonce(n => n + 1);
                                                setFfExpanded(true);
                                            }
                                        }}>{ffExpanded ? 'Compress All' : 'Expand All'}</button>
                            </div>
                        )}
                        {activeTab === 'fulfillments' && (
                            <div className="odv2-segctl" style={{ marginLeft: 8 }}>
                                <button className={`odv2-segctl-btn ${ffFilter === 'all' ? 'odv2-segctl-btn--active' : ''}`}
                                        onClick={() => setFfFilter('all')}>All</button>
                                <button className={`odv2-segctl-btn ${ffFilter === 'fulfilled' ? 'odv2-segctl-btn--active' : ''}`}
                                        onClick={() => setFfFilter('fulfilled')}>Fulfilled</button>
                                <button className={`odv2-segctl-btn ${ffFilter === 'unfulfilled' ? 'odv2-segctl-btn--active' : ''}`}
                                        onClick={() => setFfFilter('unfulfilled')}>Unfulfilled</button>
                            </div>
                        )}
                    </div>
                        );
                    })()}

                    {/* Modify-mode action bar lives INSIDE each tree's <thead>
                        (just above the column-header row) so the select-all
                        dots align with the Picked / Pressed columns. */}

                    {/* Keep all trees mounted so collapse/expand state survives
                        tab switches. Inactive trees are hidden with display:none. */}
                    <div style={{ display: activeTab === 'players' ? '' : 'none' }}>
                        <ItemsTree bundles={bundles}
                                   portalState={portalState}
                                   fulfillmentShipped={fulfillmentShipped}
                                   shippedLineIds={shippedLineIds}
                                   priceOf={priceOf}
                                   density={density}
                                   roster={roster}
                                   subSections={subSections}
                                   order={order}
                                   expandAllNonce={expandAllNonce}
                                   expandPlayersNonce={expandPlayersNonce}
                                   compressAllNonce={compressAllNonce}
                                   onUserToggle={() => setPlayersStage('collapsed')}
                                   modifyMode={modifyMode}
                                   pendingChanges={pendingChanges}
                                   onTogglePending={togglePending}
                                   modifyBar={modifyMode ? {
                                       pendingChanges,
                                       pendingSubmitting,
                                       cancelModify,
                                       applyPending,
                                       selectAllTargets,
                                       bigPickedState,
                                       bigPressedState,
                                       onSelectAllStage: selectAllStage,
                                   } : null} />
                    </div>
                    <div style={{ display: activeTab === 'fulfillments' ? '' : 'none' }}>
                        {ffSubSections.length === 0 && subSections.length > 0 ? (
                            <div className="odv2-empty-state">
                                {ffFilter === 'unfulfilled'
                                    ? 'No unfulfilled items — all fulfillments are complete.'
                                    : ffFilter === 'fulfilled'
                                        ? 'No fulfilled items yet.'
                                        : 'No fulfillments to display.'}
                                {ffFilter !== 'all' && (
                                    <>
                                        {' '}
                                        <button type="button" className="odv2-empty-state-link"
                                                onClick={() => setFfFilter('all')}>Show all</button>
                                    </>
                                )}
                            </div>
                        ) : (
                            <FulfillmentsTree bundles={bundles}
                                              portalState={portalState}
                                              fulfillmentShipped={fulfillmentShipped}
                                              shippedLineIds={shippedLineIds}
                                              priceOf={priceOf}
                                              density={density}
                                              subSections={ffSubSections}
                                              sortMode={ffSortMode}
                                              order={order}
                                              expandAllNonce={expandAllNonce}
                                              compressAllNonce={compressAllNonce}
                                              onUserToggle={() => setFfExpanded(false)}
                                              modifyMode={modifyMode}
                                              pendingChanges={pendingChanges}
                                              onTogglePending={togglePending}
                                              modifyBar={modifyMode ? {
                                                  pendingChanges,
                                                  pendingSubmitting,
                                                  cancelModify,
                                                  applyPending,
                                                  selectAllTargets,
                                                  bigPickedState,
                                                  bigPressedState,
                                                  onSelectAllStage: selectAllStage,
                                              } : null} />
                        )}
                    </div>
                    {/* Order Summary lives at the bottom of the items panel
                        on Players + Fulfillments tabs — same as V1. */}
                    {!TL_TABS.has(activeTab) && (
                        <OrderSummarySection order={order} lineItems={lineItems} />
                    )}
                    {visitedTabs.has('eventlog') && (
                        <div className="odv2-timeline-log odv2-timeline-log--full" style={{ display: activeTab === 'eventlog' ? '' : 'none' }}>
                            <EventLog orderId={orderId} cancelReason={order.cancelReason} perPage={50} title="" expandMode />
                        </div>
                    )}
                    {visitedTabs.has('visual') && (
                        <div className="odv2-timeline-wrap" style={{ display: activeTab === 'visual' ? '' : 'none' }}>
                            <OrderTimeline orderId={orderId}
                                           order={order}
                                           fulfillBy={fulfillByRaw}
                                           initialViewStart={tlView.start}
                                           initialViewEnd={tlView.end}
                                           fetchFn={_storeFetch}
                                           embedded
                                           onClose={null} />
                        </div>
                    )}
                </div>

                {!TL_TABS.has(activeTab) && (
                <div className="odv2-side">
                    {sidebarHeadFn
                        ? sidebarHeadFn({ order })
                        : <WorkflowRail totals={pickPressTotals}
                                        createdAt={createdAt}
                                        fulfillBy={fulfillBy}
                                        printed={isOrderPrinted} />
                    }

                    <OrderInfoCard order={order} />

                    {String(order.note || '').trim() && (
                        <div className="odv2-card odv2-note-card">
                            <h3 className="odv2-card-title">Order Notes</h3>
                            <p className="odv2-note-text">{order.note}</p>
                        </div>
                    )}

                    {Array.isArray(order.tags) && order.tags.length > 0 && (
                        <TagsCard tags={order.tags} />
                    )}
                </div>
                )}
            </div>

            </FoMenuContext.Provider>
            </NpContext.Provider>
            </JumbleContext.Provider>
            </LineFlagsContext.Provider>
            </TpReqContext.Provider>
            </TpOverridesContext.Provider>
            </SkuNotificationsContext.Provider>
            </StoreKeyContext.Provider>

            {/* Prev / Next — same footer as V1, only when the operator came from the Orders list */}
            {prevNextCursor.idx >= 0 && prevNextCursor.ids.length > 1 && (
                <div className="od-prevnext odv2-prevnext">
                    <div className="ots-pagination-info">
                        <strong>Order {prevNextCursor.idx + 1}</strong>
                        <span> of {prevNextCursor.ids.length.toLocaleString()}</span>
                    </div>
                    <div className="ots-pagination-btns">
                        <button type="button" className="ots-pagination-btn" disabled={!prevOrderId}
                                onClick={() => prevOrderId && navigate(`/store/${prevNextCursor.store}/orders/${prevOrderId}?v=2`, { state: { backTo: backToHref } })}
                                title={prevOrderId ? `Go to order ${prevOrderId}` : 'No previous order in this list'}>← Previous</button>
                        <button type="button" className="ots-pagination-btn" disabled={!nextOrderId}
                                onClick={() => nextOrderId && navigate(`/store/${prevNextCursor.store}/orders/${nextOrderId}?v=2`, { state: { backTo: backToHref } })}
                                title={nextOrderId ? `Go to order ${nextOrderId}` : 'No next order in this list'}>Next →</button>
                    </div>
                </div>
            )}

            {addrReviewOpen && (() => {
                const d = addrReview; const cur = d?.current; const sug = d?.suggestion; const fi = d?.field_issues || {};
                const flag = (k) => fi[k] ? 'od-addr-hl od-addr-hl--warn' : '';
                const fmtA = (a) => !a ? null : (
                    <div className="od-addr-block">
                        <div>{a.firstName} {a.lastName}</div>
                        <div className={flag('address1')}>{addrLine(a)}</div>
                        <div><span className={flag('city')}>{a.city}</span>, <span className={flag('provinceCode')}>{a.provinceCode}</span> <span className={flag('zip')}>{a.zip}</span></div>
                        <div>{a.countryCode}</div>
                    </div>
                );
                return (
                    <div className="od-addr-modal-bg" onClick={() => !addrBusy && setAddrReviewOpen(false)}>
                        <div className="od-addr-modal" onClick={e => e.stopPropagation()}>
                            <div className="od-addr-modal-head">
                                <strong>Review address issues</strong>
                                <button type="button" className="od-addr-close" onClick={() => setAddrReviewOpen(false)} aria-label="Close">×</button>
                            </div>
                            {!d && !addrErr && <div className="od-addr-loading">Checking the address…</div>}
                            {addrErr && <div className="od-addr-err">{addrErr}</div>}
                            {d && addrMode === 'review' && (
                                <div className="od-addr-body">
                                    {d.summary && <div className="od-addr-summary">{d.summary}</div>}
                                    <div className="od-addr-cols">
                                        {sug && (
                                            <label className={`od-addr-opt${addrChoice === 'suggestion' ? ' od-addr-opt--on' : ''}`}>
                                                <input type="radio" name="addrChoice" checked={addrChoice === 'suggestion'} onChange={() => setAddrChoice('suggestion')} />
                                                <div><div className="od-addr-opt-title">Suggested</div>{fmtA(sug)}</div>
                                            </label>
                                        )}
                                        <label className={`od-addr-opt${addrChoice === 'current' ? ' od-addr-opt--on' : ''}`}>
                                            <input type="radio" name="addrChoice" checked={addrChoice === 'current'} onChange={() => setAddrChoice('current')} />
                                            <div><div className="od-addr-opt-title">Entered by customer</div>{fmtA(cur)}</div>
                                        </label>
                                    </div>
                                    <label className="od-addr-save"><input type="checkbox" checked={addrSaveCust} onChange={e => setAddrSaveCust(e.target.checked)} /> Also update the customer's saved address</label>
                                    <div className="od-addr-actions">
                                        <button type="button" className="od-addr-btn" onClick={() => setAddrMode('edit')}>Edit address</button>
                                        <button type="button" className="od-addr-btn od-addr-btn--primary" disabled={addrBusy}
                                                onClick={() => applyAddress(addrChoice === 'suggestion' ? sug : cur, addrChoice)}>
                                            {addrBusy ? 'Saving…' : `Use ${addrChoice === 'suggestion' ? 'suggested' : 'entered'} address`}
                                        </button>
                                    </div>
                                </div>
                            )}
                            {d && addrMode === 'edit' && addrForm && (
                                <div className="od-addr-body">
                                    <div className="od-addr-form">
                                        {[['firstName', 'First name'], ['lastName', 'Last name'], ['address1', 'Address'], ['address2', 'Apt / suite'], ['city', 'City'], ['provinceCode', 'State'], ['zip', 'ZIP'], ['countryCode', 'Country'], ['phone', 'Phone']].map(([k, label]) => (
                                            <label key={k} className="od-addr-field"><span>{label}</span>
                                                <input value={addrForm[k] || ''} onChange={e => setAddrForm(f => ({ ...f, [k]: e.target.value }))} />
                                            </label>
                                        ))}
                                    </div>
                                    <label className="od-addr-save"><input type="checkbox" checked={addrSaveCust} onChange={e => setAddrSaveCust(e.target.checked)} /> Also update the customer's saved address</label>
                                    <div className="od-addr-actions">
                                        <button type="button" className="od-addr-btn" onClick={() => setAddrMode('review')}>Back</button>
                                        <button type="button" className="od-addr-btn od-addr-btn--primary" disabled={addrBusy} onClick={() => applyAddress(addrForm, 'edited')}>{addrBusy ? 'Saving…' : 'Save address'}</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })()}

            {/* Hidden print clone — captured into popup window by handlePrint.
                Reuses V1's .od-print-clone CSS (fixed off-screen, hidden). */}
            {/* @font-face rules for every number-preview font the order references
                (V1 parity) — lets embellishment thumbs render the player number
                in the product's own typeface. */}
            {(() => { const css = buildFontFaceCss(numberPreviews); return css ? <style>{css}</style> : null; })()}
            <div ref={printSourceRef} className="od-print-clone" aria-hidden="true">
                {printMergedLocs ? (
                    <MergedNjPrint
                        fo={{ order: {}, location_name: order?._location_name || '' }}
                        fullOrder={order}
                        locs={printMergedLocs}
                        printAll={printFull}
                        numberPreviews={printPreviews}
                        storeKey={activeStore || storeSlug || 'soccerpost'}
                        storeDisplayName={_storeNames?.[activeStore || storeSlug] || 'Soccer Post'}
                        printoutFooterHtml={printoutFooterHtml}
                        skuNotifications={skuNotifications}
                    />
                ) : (
                <OrderPrintCard
                    fo={{ order: {}, location_name: order?._location_name || '' }}
                    fullOrder={order}
                    printAll={printFull}
                    bins={printBins}
                    overstockBins={printOverstockBins}
                    numberPreviews={printPreviews}
                    locationId={odPrintLocationId}
                    storeKey={activeStore || storeSlug || 'soccerpost'}
                    storeDisplayName={_storeNames?.[activeStore || storeSlug] || 'Soccer Post'}
                    printoutFooterHtml={printoutFooterHtml}
                    skuNotifications={skuNotifications}
                />
                )}
            </div>

            {/* Already-printed confirmation */}
            {showPrintConfirm && (
                <div className="odv2-modal-backdrop" onClick={() => setShowPrintConfirm(false)}>
                    <div className="odv2-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="odv2-modal-title">Order already printed</div>
                        <div className="odv2-modal-body">
                            This order has already been printed. Are you sure you want to print it again?
                            <div style={{ marginTop: 12 }}>
                                <Checkbox
                                    label="Reprint full order, not just unfulfilled items"
                                    helpText="Every line at its ordered quantity — logos and all — even those already fulfilled."
                                    checked={printFull}
                                    onChange={setPrintFull}
                                />
                            </div>
                        </div>
                        <div className="odv2-modal-actions">
                            <button className="odv2-btn" onClick={() => setShowPrintConfirm(false)}>Cancel</button>
                            <button className="odv2-btn odv2-btn--primary" onClick={handlePrintConfirmed}>Print again</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 2026-09-26 (Eric): Print order — every print of an order with open items at both NJ warehouses. */}
            {showPrintOptions && (
                <div className="odv2-modal-backdrop" onClick={() => setShowPrintOptions(false)}>
                    <div className="odv2-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="odv2-modal-title">Print order</div>
                        <div className="odv2-modal-body">
                            {isOrderPrinted && <div style={{ marginBottom: 10 }}>This order has already been printed. Printing it again.</div>}
                            <div style={{ fontWeight: 600, marginBottom: 12 }}>{order?.replacement_alias || order?.name}</div>
                            <Checkbox
                                label="Merge NJ Warehouses"
                                helpText="One ticket: the Tinton Falls Warehouse items first, a dividing line, then the Eatontown Warehouse items. The order is tagged as printed at both warehouses."
                                checked={mergeNj}
                                onChange={setMergeNj}
                            />
                            {isOrderPrinted && (
                                <div style={{ marginTop: 12 }}>
                                    <Checkbox
                                        label="Reprint full order, not just unfulfilled items"
                                        helpText="Every line at its ordered quantity — logos and all — even those already fulfilled."
                                        checked={printFull}
                                        onChange={setPrintFull}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="odv2-modal-actions">
                            <button className="odv2-btn" onClick={() => setShowPrintOptions(false)}>Cancel</button>
                            <button className="odv2-btn odv2-btn--primary" onClick={() => startPrint(mergeNj)}>Print</button>
                        </div>
                    </div>
                </div>
            )}

            {/* On-hold HARD BLOCK — no "Print anyway" escape. */}
            {showOnHoldBlock && (
                <div className="odv2-modal-backdrop" onClick={() => setShowOnHoldBlock(false)}>
                    <div className="odv2-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="odv2-modal-title">Order is on hold</div>
                        <div className="odv2-modal-body">
                            This order is <strong>on hold in Shopify</strong> and cannot be printed. Release the hold in Shopify, then come back to print.
                        </div>
                        <div className="odv2-modal-actions">
                            <button className="odv2-btn odv2-btn--primary" onClick={() => setShowOnHoldBlock(false)}>OK</button>
                        </div>
                    </div>
                </div>
            )}

            {/* All-Locations toast */}
            {printToast && (
                <div className="odv2-toast" role="status">{printToast.msg}</div>
            )}

            {/* Refund modal — only mounted when open (saves render cost) */}
            {showRefundModal && (
                <RefundModal
                    order={order}
                    storeKey={activeStore || storeSlug || 'soccerpost'}
                    onClose={() => setShowRefundModal(false)}
                    onRefundIssued={() => {
                        setShowRefundModal(false);
                        refreshOrder();
                    }}
                />
            )}
            {reportIssueOpen && (
                <ReportIssueModal
                    orderName={order?.name}
                    onSubmit={async (reason, note) => {
                        const numericId = String(orderId).split('/').pop();
                        const sk = activeStore || storeSlug || 'soccerpost';
                        const r = await _storeFetch(`/api/orders/${numericId}/report-issue?store=${encodeURIComponent(sk)}`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ store_key: sk, reason, note }),
                        });
                        if (!r.ok) {
                            const d = await r.json().catch(() => ({}));
                            throw new Error(d.detail || `HTTP ${r.status}`);
                        }
                        setReportIssueOpen(false);
                        refreshOrder();
                    }}
                    onCancel={() => setReportIssueOpen(false)}
                />
            )}

            {/* Change Pickup Location modal (Tools menu) */}
            <Modal
                open={showPickupLocModal}
                onClose={() => setShowPickupLocModal(false)}
                title="Change Pickup Location"
                primaryAction={{
                    content: pickupLocSaving ? 'Saving…' : 'Save',
                    onAction: handlePickupLocSave,
                    disabled: !pickupLocSelected || pickupLocSaving || pickupLocLoading,
                    loading: pickupLocSaving,
                }}
                secondaryActions={[
                    ...(order?.store_pickup_override && pickupLocSelected === order.store_pickup_override ? [{
                        content: 'Clear override / Use original',
                        onAction: handlePickupLocClear,
                        disabled: pickupLocSaving,
                    }] : []),
                    { content: 'Cancel', onAction: () => setShowPickupLocModal(false) },
                ]}
            >
                <Modal.Section>
                    <BlockStack gap="400">
                        {pickupLocError && (
                            <Banner tone="critical" onDismiss={() => setPickupLocError(null)}>
                                <Text as="p" variant="bodyMd">{pickupLocError}</Text>
                            </Banner>
                        )}
                        {pickupLocLoading ? (
                            <Text as="p" variant="bodyMd" tone="subdued">Loading locations…</Text>
                        ) : (
                            pickupLocList.length === 0 ? (
                                <Banner tone="warning">
                                    <Text as="p" variant="bodyMd">None of your locations is set up as a pickup location yet. Ask Soccer Post to add it.</Text>
                                </Banner>
                            ) : <Select
                                label="Pickup location"
                                placeholder="Choose a location…"
                                options={pickupLocList.map(loc => ({ label: loc, value: loc }))}
                                value={pickupLocSelected}
                                onChange={setPickupLocSelected}
                            />
                        )}
                    </BlockStack>
                </Modal.Section>
            </Modal>
        </div>
    );
}
